// TrialOnboarding.tsx
// ---------------------------------------------------------------------------
// Guest "Try it before you commit" flow.
//
// This is NOT a separate clone. The trial drops the guest onto a real Roster
// STAGE built from the genuine leaf components: the actual `RosterCharacterTile`
// and the actual `AvailabilityGrid` (timezone-correct via `useTimezone`). A
// step-locked spotlight hand-holds them: "this is where your characters live →
// add your main → now paint when you can raid". Nothing is written; the inputs
// are captured into a sessionStorage draft (trialReplay.ts) as the exact bodies
// the real endpoints accept, and replayed after Discord signup.
//
//   mode="trial" is the no-account walkthrough (rendered from the Landing page)
//   mode="replay" runs after the Discord bounce: replays the draft, then
//                   explains how to wire up the guild Discord bot.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { API_URL } from "./config";
import AvailabilityGrid from "./components/AvailabilityGrid";
import { RosterCharacterTile } from "./RosterPage";
import { PickerTzLabel } from "./components/PickerTzLabel";
import { useTimezone } from "./lib/TimezoneContext";
import { getCurrentResetWeek, getNextResetWeek } from "./lib/time";
import { characterLogsUrl, useGameLogs } from "./lib/gameLogs";
import {
  TrialDraft,
  CharacterPayload,
  GuildPayload,
  ReplayStep,
  loadDraft,
  saveDraft,
  clearDraft,
  newDraft,
  markReplayPending,
  clearReplayPending,
  replayDraft,
} from "./trialReplay";

// --- Game-option schema types (mirror RosterPage) --------------------------
interface Game { id: string; code: string; name: string; }
interface Option { id: number; key: string; label: string; metadata: any; image_url?: string | null; }
interface Group { id: number; key: string; label: string; multiSelect: boolean; isRequired: boolean; options: Option[]; }

const TOTAL_AVAIL_SLOTS = 7 * 48;

// --- small helpers (replicated from RosterPage so the capture matches) ------
function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
function stringToColor(str: string): string {
  if (!str) return "#525252";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "00000".substring(0, 6 - c.length) + c;
}
function boolArrayToBitstring(arr: boolean[]): string {
  const out: string[] = [];
  for (let i = 0; i < TOTAL_AVAIL_SLOTS; i++) out.push(arr[i] ? "1" : "0");
  return out.join("");
}
// Reset-week helpers now come from the shared implementation. These were a
// private copy of the "Wednesday 00:00 UTC" rule, which is EU-shaped and a day
// out for US realms. Call with a rule (src/lib/regionReset.ts) for the viewer's
// real reset; the default is EU, matching the previous behaviour exactly.
// Guests have no character yet, so no region: EU is the only sensible default here.
const getCurrentWednesday = () => getCurrentResetWeek();
const getNextWednesday = () => getNextResetWeek();
function bumpHalfHour(hhmm: string, deltaMin: number): string {
  const [h, m] = (hhmm || "20:00").split(":").map(Number);
  const total = (((h * 60 + m + deltaMin) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
// Mirror GuildsPage so the captured guild satisfies the faction CHECK constraint.
function formatFaction(f: string | null | undefined): string | null {
  if (!f || !f.trim()) return null;
  const lower = f.trim().toLowerCase();
  if (lower === "alliance") return "Alliance";
  if (lower === "horde") return "Horde";
  if (lower === "neutral") return "Neutral";
  return f.charAt(0).toUpperCase() + f.slice(1);
}
function factionColor(f: string | null | undefined): string {
  const v = (f || "").toLowerCase();
  if (v.includes("alliance")) return "#3b82f6";
  if (v.includes("horde")) return "#ef4444";
  return "#eab308";
}
// Mirrors GuildsPage.getFactionGradient (used by the guild detail hero).
function factionGradient(f: string | null | undefined): string {
  const v = (f || "").toLowerCase();
  if (v.includes("alliance")) return "from-blue-600/20 to-blue-900/5";
  if (v.includes("horde")) return "from-red-600/20 to-red-900/5";
  return "from-amber-500/20 to-amber-900/5";
}

// === Shared styling =========================================================
const inputCls =
  "w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 outline-none placeholder:text-white/20";
const selectCls =
  "w-full appearance-none rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-xs text-white focus:border-cyan-500 focus:outline-none";
const labelCls = "text-[10px] uppercase font-bold text-white/40 tracking-wider";

/** Clean instruction card for the guided tour (no mascot). `pose` is ignored. */
function Coach({ children }: { pose?: string; children: React.ReactNode; compact?: boolean }) {
  return (
    <div className="relative flex items-start gap-3 rounded-2xl border border-cyan-500/20 bg-[#11151c] shadow-xl px-5 py-4">
      <div className="mt-0.5 h-6 w-6 shrink-0 rounded-md bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
        <svg className="w-3.5 h-3.5 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      </div>
      <div className="flex-1 text-sm text-white/80 leading-relaxed">{children}</div>
    </div>
  );
}

function PrimaryBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-white transition-all ${
        disabled ? "bg-cyan-700/40 cursor-not-allowed" : "bg-cyan-600 hover:bg-cyan-500 shadow-lg shadow-cyan-900/30"
      }`}>{children}</button>
  );
}
function GhostBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-white/50 hover:text-white hover:bg-white/5 transition-colors">{children}</button>
  );
}
function DiscordBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] text-xs font-bold uppercase tracking-widest text-white shadow-lg shadow-[#5865F2]/20 transition-all">
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z" /></svg>
      {children}
    </button>
  );
}

// === Component ==============================================================
const STEPS = ["intro", "characters", "availability", "guildPrompt", "guild", "guildInvite", "plannerNav", "raid", "planner", "keep"] as const;

// Sample invite code shown in the trial's guild Recruit panel (real codes are
// generated server-side on signup).
const TRIAL_INVITE_CODE = "DEMO42";
type Step = (typeof STEPS)[number];

// WoW class colors (matches App.tsx CLASS_COLORS) for the mock planner roster.
const CLASS_COLORS: Record<string, string> = {
  Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
  Priest: "#FFFFFF", "Death Knight": "#C41F3B", Shaman: "#0070DE", Mage: "#69CCF0",
  Warlock: "#9482C9", Monk: "#00FF96", Druid: "#FF7D0A", "Demon Hunter": "#A330C9", Evoker: "#33937F",
};
const ROLE_COLORS: Record<string, string> = { Tank: "#38bdf8", Healer: "#34d399", "Melee DPS": "#fb923c", "Ranged DPS": "#fb923c", DPS: "#fb923c" };

// --- App shell replica ------------------------------------------------------
// The exact tab set a regular (non-admin) user sees in App.tsx, with the same
// icons. The trial shows the full nav so the app looks identical, but tabs are
// step-locked: clicking one that isn't the current tour stop pops a notice.
const NAV_TABS = ["Home", "Roster", "Guilds", "Planner", "Find"] as const;
function navIcon(t: string) {
  const props = { className: "w-4 h-4", fill: "none" as const, viewBox: "0 0 24 24", stroke: "currentColor" };
  switch (t) {
    case "Home": return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
    case "Roster": return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
    case "Guilds": return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
    case "Planner": return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
    case "Find": return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
  }
  return null;
}
// Identical to App.tsx TabButton. `attention` adds a pinging dot when the tour
// wants the player to click this tab (tutorial cue, not present in the real app).
function ShellTabButton({ label, icon, active, attention, tt, onClick }: { label: string; icon: React.ReactNode; active: boolean; attention?: boolean; tt?: string; onClick: () => void }) {
  return (
    <button
      data-tt={tt}
      onClick={onClick}
      className={`
        relative px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-all duration-200 flex items-center gap-2
        ${active
          ? "bg-white/10 text-white shadow-lg shadow-white/5 ring-1 ring-white/20"
          : "text-white/40 hover:text-white hover:bg-white/5"
        }
      `}
    >
      {icon}
      <span className="hidden xl:block">{label}</span>
      {attention && (
        <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-400" />
        </span>
      )}
    </button>
  );
}
// What each locked tab says when clicked. Tabs outside the tour explain that
// they unlock with an account; tour tabs point back at the guide.
const NAV_NOTICES: Record<string, { title: string; body: string; locked: boolean }> = {
  Home: { locked: true, title: "Home unlocks with your account", body: "Your dashboard lives here: upcoming raids, invites and guild announcements. Sign up free with Discord and it's yours." },
  Find: { locked: true, title: "Find unlocks with your account", body: "The group finder: browse real players and guilds recruiting right now. Available as soon as you sign up." },
  Roster: { locked: false, title: "The tour comes back here", body: "Your roster is a stop on this walkthrough. Use Back and Next in the guide below to move between stops." },
  Guilds: { locked: false, title: "This stop is on the tour", body: "Guilds are a stop on this walkthrough. Use Back and Next in the guide below to move between stops." },
  Planner: { locked: false, title: "This stop is on the tour", body: "The raid planner is the finale of this walkthrough. Keep going with the guide below to reach it." },
  ARC: { locked: true, title: "Auto Schedule unlocks with your account", body: "ARC reads your whole guild's availability and builds complete, balanced raids in one click. Sign up free with Discord to use it." },
  RAIDTOOLS: { locked: true, title: "Raid tools unlock with your account", body: "Soft Reserves, Assignments, Loot Council, player ratings and raid editing all live here once you sign up. Free, via Discord." },
  DISCORD: { locked: true, title: "The Discord bot unlocks with your account", body: "Invite the Rallify bot to your guild's server and it DMs members each reset to collect availability, then posts your raids automatically." },
  GUILDTOOLS: { locked: true, title: "Guild management unlocks with your account", body: "The guild dashboard, settings, logo and member management all live here once you sign up. Free, via Discord." },
};
// Mock guildmates that fill out the planner roster alongside the player (fake data, never saved).
const MOCK_RAIDERS: { name: string; cls: string; role: string; spec: string; pug?: boolean }[] = [
  { name: "Dawnguard", cls: "Paladin", role: "Tank", spec: "Protection" },
  { name: "Tidecaller", cls: "Shaman", role: "Healer", spec: "Restoration" },
  { name: "Wavecrest", cls: "Priest", role: "Healer", spec: "Holy" },
  { name: "Ironhide", cls: "Warrior", role: "Melee DPS", spec: "Fury" },
  { name: "Frostbite", cls: "Mage", role: "Ranged DPS", spec: "Frost" },
  { name: "Hexfire", cls: "Warlock", role: "Ranged DPS", spec: "Affliction" },
  { name: "Swiftarrow", cls: "Hunter", role: "Ranged DPS", spec: "Marksmanship", pug: true },
];

// WCL parse color scale (identical to CreateRaidPage.getParseColor).
function getParseColor(score: number | undefined): string {
  if (score === undefined) return "#555";
  if (score === 100) return "#e5cc80";
  if (score >= 99) return "#e268a8";
  if (score >= 95) return "#ff8000";
  if (score >= 75) return "#a335ee";
  if (score >= 50) return "#0070dd";
  if (score >= 25) return "#1eff00";
  return "#9d9d9d";
}
// Role icons (identical to CreateRaidPage.getRoleIcon).
function getRoleIcon(role: string): string {
  switch (role?.trim()) {
    case "Tank": return "🛡️";
    case "Healer": return "💚";
    case "Melee DPS": return "⚔️";
    case "Ranged DPS": return "🏹";
    default: return "👤";
  }
}
const SERIES_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

// Roster Pool for the Create Raid "Roster" tab. Each player has draggable spec
// entries (character + class + spec + role + parse score), mirroring
// CreateRaidPage's player bench cards (specs on alts carry the alt's name).
interface SpecEntry { char: string; cls: string; spec: string; role: string; score: number; }
interface PoolPlayer { name: string; more?: number; squad?: number; specs: SpecEntry[]; }
const MOCK_POOL: PoolPlayer[] = [
  { name: "Butterash", more: 1, specs: [
    { char: "Butterash", cls: "Warrior", spec: "Protection", role: "Tank", score: 77 },
    { char: "Butterash", cls: "Warrior", spec: "Fury", role: "Melee DPS", score: 77 },
    { char: "Butterash", cls: "Warrior", spec: "Arms", role: "Melee DPS", score: 77 },
    { char: "Butterstorm", cls: "Shaman", spec: "Enhancement", role: "Melee DPS", score: 25 },
    { char: "Butterstorm", cls: "Shaman", spec: "Elemental", role: "Ranged DPS", score: 25 },
  ] },
  { name: "Manaplix", more: 1, squad: 5, specs: [
    { char: "Manaplix", cls: "Paladin", spec: "Protection", role: "Tank", score: 78 },
    { char: "Manaheal", cls: "Priest", spec: "Holy", role: "Healer", score: 78 },
    { char: "Manaplix", cls: "Warlock", spec: "Destruction", role: "Ranged DPS", score: 36 },
  ] },
  { name: "Høst", squad: 9, specs: [
    { char: "Høst", cls: "Paladin", spec: "Protection", role: "Tank", score: 68 },
    { char: "Høst", cls: "Warrior", spec: "Fury", role: "Melee DPS", score: 68 },
  ] },
  { name: "Trenden", more: 1, squad: 5, specs: [
    { char: "Trenden", cls: "Druid", spec: "Restoration", role: "Healer", score: 60 },
    { char: "Trendshot", cls: "Hunter", spec: "Beast Mastery", role: "Ranged DPS", score: 91 },
  ] },
  { name: "Hexyy", squad: 5, specs: [
    { char: "Hexyy", cls: "Druid", spec: "Restoration", role: "Healer", score: 92 },
    { char: "Hexyy", cls: "Shaman", spec: "Elemental", role: "Ranged DPS", score: 92 },
  ] },
  { name: "Naegativ", squad: 4, specs: [
    { char: "Naegativ", cls: "Druid", spec: "Restoration", role: "Healer", score: 47 },
    { char: "Naegativ", cls: "Shaman", spec: "Elemental", role: "Ranged DPS", score: 47 },
  ] },
  { name: "Bombadått", squad: 9, specs: [
    { char: "Bombadått", cls: "Warlock", spec: "Destruction", role: "Ranged DPS", score: 71 },
  ] },
  { name: "frosttoe", specs: [
    { char: "frosttoe", cls: "Mage", spec: "Arcane", role: "Ranged DPS", score: 79 },
  ] },
];

// Unavailable-reason buckets for the planner Roster Pool (identical labels and
// colors to PlannerPage's bucketConfig) + a few mock players to fill them.
const BUCKET_CONFIG: Record<string, { label: string; color: string; dotColor: string }> = {
  "In Another Raid": { label: "In Another Raid", color: "text-cyan-400/70", dotColor: "bg-cyan-400" },
  "Alt Signed": { label: "Alt Already Signed", color: "text-amber-400/70", dotColor: "bg-amber-400" },
  "Not Confirmed": { label: "Availability Unconfirmed", color: "text-orange-400/70", dotColor: "bg-orange-400" },
  "Unavailable": { label: "Time Conflict", color: "text-red-400/70", dotColor: "bg-red-400" },
  "Locked Out": { label: "Raid Locked", color: "text-purple-400/70", dotColor: "bg-purple-400" },
  "Not Interested": { label: "Not Signed Up", color: "text-white/30", dotColor: "bg-white/40" },
};
const MOCK_UNAVAILABLE: { name: string; cls: string; score: number; roles: string[]; reason: string }[] = [
  { name: "Shadowfax", cls: "Priest", score: 64, roles: ["Ranged DPS"], reason: "Not Confirmed" },
  { name: "Gravemind", cls: "Warlock", score: 88, roles: ["Ranged DPS"], reason: "Locked Out" },
  { name: "Pixelbow", cls: "Hunter", score: 41, roles: ["Ranged DPS"], reason: "Not Interested" },
];

// Mock buff/debuff coverage tags for the Composition Coverage panel (mirrors
// CreateRaidPage's panel; covered = a seated character's class provides it).
const TRIAL_COVERAGE: { category: string; tags: { label: string; providers: string[] }[] }[] = [
  { category: "Buffs", tags: [
    { label: "Battle Shout", providers: ["Warrior"] },
    { label: "Blessing of Kings", providers: ["Paladin"] },
    { label: "Mark of the Wild", providers: ["Druid"] },
    { label: "Arcane Intellect", providers: ["Mage"] },
    { label: "Power Word: Fortitude", providers: ["Priest"] },
    { label: "Windfury Totem", providers: ["Shaman"] },
    { label: "Trueshot Aura", providers: ["Hunter"] },
  ] },
  { category: "Debuffs", tags: [
    { label: "Sunder Armor", providers: ["Warrior"] },
    { label: "Curse of the Elements", providers: ["Warlock"] },
    { label: "Faerie Fire", providers: ["Druid"] },
  ] },
];

interface TrialOnboardingProps {
  mode: "trial" | "replay";
  onExit: () => void;
  onComplete: (createdGuild: any | null) => void;
}

export default function TrialOnboarding({ mode, onExit, onComplete }: TrialOnboardingProps) {
  const timezone = useTimezone();

  // schema
  const [games, setGames] = useState<Game[]>([]);
  const [fieldsByGame, setFieldsByGame] = useState<Record<string, Group[]>>({});
  const [gameId, setGameId] = useState("");
  const gameLogs = useGameLogs(gameId);
  const groups = fieldsByGame[gameId] || [];
  const groupByKey = useCallback((key: string) => groups.find((g) => g.key === key), [groups]);

  // walkthrough
  const [step, setStep] = useState(0);
  const cur: Step = STEPS[step];

  // app-shell nav: clicking a tab that isn't the current tour stop shows a
  // notice under the header instead of navigating (guests are step-locked)
  const [navNotice, setNavNotice] = useState<string | null>(null);
  const navNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (navNoticeTimer.current) clearTimeout(navNoticeTimer.current); }, []);

  // character form
  const [modalOpen, setModalOpen] = useState(false);
  const [charName, setCharName] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number | number[]>>({});
  const [readyToRaid, setReadyToRaid] = useState(false);
  const [wclLink, setWclLink] = useState("");
  const [charError, setCharError] = useState<string | null>(null);
  const [createdChar, setCreatedChar] = useState<any | null>(null);

  // availability
  const [availSlots, setAvailSlots] = useState<boolean[]>(() => new Array(TOTAL_AVAIL_SLOTS).fill(false));
  const weekStart = useMemo(() => getNextWednesday(), []);
  const availCount = availSlots.filter(Boolean).length;

  // guild form (mirrors GuildsPage "Create Guild")
  const [guildModalOpen, setGuildModalOpen] = useState(false);
  const [guildCreated, setGuildCreated] = useState(false);
  const [gName, setGName] = useState("");
  const [gTag, setGTag] = useState("");
  const [gRegion, setGRegion] = useState("");
  const [gRealm, setGRealm] = useState("");
  const [gFaction, setGFaction] = useState("");
  const [gAbout, setGAbout] = useState("");
  const [gSchedule, setGSchedule] = useState("");
  const [gContact, setGContact] = useState("");
  const [guildError, setGuildError] = useState<string | null>(null);
  const [directory, setDirectory] = useState<any[]>([]);

  // guild directory stage (mirrors GuildsPage list-view controls)
  const [guildSearch, setGuildSearch] = useState("");
  const [myGuildsOnly, setMyGuildsOnly] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joinCharId, setJoinCharId] = useState("");
  const [joinNote, setJoinNote] = useState(false);

  // guild detail stage (Recruit panel): two-phase sub-step, first the invite
  // code is spotlit, then the Planner tab so the player navigates themselves
  const [invitePhase, setInvitePhase] = useState<"code" | "nav">("code");
  useEffect(() => { if (cur === "guildInvite") setInvitePhase("code"); }, [cur]);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // raid mockup (emulation only, never saved) — state mirrors CreateRaidPage
  const [raidOptId, setRaidOptId] = useState<number | "">("");
  const [raidLabel, setRaidLabel] = useState("");
  const [raidBuilt, setRaidBuilt] = useState(false);
  const [raidTab, setRaidTab] = useState(0); // 0 Setup, 1 Roster, 2 Review
  const [pickedTime, setPickedTime] = useState(0);
  const [scheduleMode, setScheduleMode] = useState<"single" | "multi" | "series">("single");
  const [sessions, setSessions] = useState<{ date: string; start: string; end: string }[]>([{ date: "", start: "20:00", end: "23:00" }]);
  const [recurring, setRecurring] = useState<Record<string, { start: string; end: string }>>({});
  const [chainedIds, setChainedIds] = useState<number[]>([]);
  const [suggestWeek, setSuggestWeek] = useState(0); // 0 = current reset, 1 = next
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [pugCopied, setPugCopied] = useState(false);
  // Drag-and-drop roster (Roster tab). 10 seats + a bench; specs dragged from the pool.
  const [seats, setSeats] = useState<(any | null)[]>(() => Array(10).fill(null));
  const [bench, setBench] = useState<any[]>([]);
  const [poolFilter, setPoolFilter] = useState<string | null>(null);
  const [dragSpec, setDragSpec] = useState<any | null>(null);
  const [buffOpen, setBuffOpen] = useState(false);
  // Review tab (mock leadership / loot)
  const [raidLeader, setRaidLeader] = useState("");
  const [lootMaster, setLootMaster] = useState("");
  const [softReserves, setSoftReserves] = useState(false);
  const [srMax, setSrMax] = useState(2);
  const [raidNotes, setRaidNotes] = useState("");
  const [releaseOnCreate, setReleaseOnCreate] = useState(false);
  const [published, setPublished] = useState(false);

  // --- Fine-grained tour spotlight ------------------------------------------
  // Each step highlights the ONE control the player should use next: the dim
  // overlay gets a hole cut around it (measured via [data-tt] selectors) and
  // the coach card anchors beside it. Falls back to a plain dim + bottom coach
  // when a step has no target (decision steps) or the target isn't mounted.
  const spotSelector: string | null = (() => {
    switch (cur) {
      case "characters": return createdChar ? '[data-tt="char-card"]' : '[data-tt="new-char"]';
      case "availability": return '[data-tt="avail-grid"]';
      case "guild": return guildCreated ? '[data-tt="own-guild-card"]' : '[data-tt="create-guild"]';
      case "guildInvite": return invitePhase === "code" ? '[data-tt="recruit-panel"]' : '[data-tt="nav-planner"]';
      case "plannerNav": return '[data-tt="schedule-raid"]';
      case "raid": return raidTab === 0 ? '[data-tt="suggested-times"]' : raidTab === 1 ? '[data-tt="roster-pool"]' : '[data-tt="publish-raid"]';
      case "planner": return '[data-tt="detail-pool"]';
      default: return null;
    }
  })();
  const [spotRect, setSpotRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useEffect(() => {
    if (mode !== "trial" || !spotSelector) { setSpotRect(null); return; }
    const measure = () => {
      const el = document.querySelector(spotSelector) as HTMLElement | null;
      if (!el) { setSpotRect(null); return; }
      const r = el.getBoundingClientRect();
      setSpotRect((prev) =>
        prev && Math.abs(prev.top - r.top) < 1 && Math.abs(prev.left - r.left) < 1 && Math.abs(prev.width - r.width) < 1 && Math.abs(prev.height - r.height) < 1
          ? prev
          : { top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    const timer = setInterval(measure, 250);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { clearInterval(timer); window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, [spotSelector, mode]);
  // Attention burst: when the spotlight jumps to a new target, flash it for a
  // couple of seconds so the eye lands there before reading the coach.
  const [spotBurst, setSpotBurst] = useState(false);
  useEffect(() => {
    if (!spotSelector) return;
    setSpotBurst(true);
    const t = setTimeout(() => setSpotBurst(false), 2400);
    return () => clearTimeout(t);
  }, [spotSelector]);

  // replay
  const [replayPhase, setReplayPhase] = useState<"replaying" | "done" | "failed">("replaying");
  const [replaySteps, setReplaySteps] = useState<ReplayStep[]>([]);
  const [createdGuild, setCreatedGuild] = useState<any | null>(null);

  const charSectionRef = useRef<HTMLElement | null>(null);
  const availSectionRef = useRef<HTMLElement | null>(null);
  const guildSectionRef = useRef<HTMLElement | null>(null);
  const guildDetailRef = useRef<HTMLElement | null>(null);
  const plannerListRef = useRef<HTMLElement | null>(null);
  const raidSectionRef = useRef<HTMLElement | null>(null);
  const plannerSectionRef = useRef<HTMLElement | null>(null);

  // Load games + fields (unauthed, work for guests). Both modes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const gRes = await fetch("/games", { credentials: "include" });
        const gs: Game[] = gRes.ok ? await gRes.json() : [];
        if (cancelled) return;
        setGames(gs);
        if (mode === "trial" && gs[0]?.id) setGameId(gs[0].id);
        const map: Record<string, Group[]> = {};
        for (const g of gs) {
          try {
            const fRes = await fetch(`/games/${g.id}/fields`, { credentials: "include" });
            if (fRes.ok) map[g.id] = await fRes.json();
          } catch { /* ignore */ }
        }
        if (!cancelled) setFieldsByGame(map);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [mode]);

  // Real guild directory so the Guilds stage shows actual guilds, like the app.
  useEffect(() => {
    if (mode !== "trial") return;
    let cancelled = false;
    fetch("/guilds/directory", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { if (!cancelled) setDirectory(Array.isArray(rows) ? rows.slice(0, 8) : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mode]);

  // WCL auto-construct (mirrors RosterPage) while the create form is open.
  useEffect(() => {
    if (!modalOpen || !charName) return;
    const realmGroup = groupByKey("realm");
    const regionGroup = groupByKey("region");
    let region = "eu", realmSlug = "";
    if (realmGroup) {
      const opt = realmGroup.options.find((o) => o.id === selectedOptions[realmGroup.id]);
      if (opt) realmSlug = opt.key || slugify(opt.label);
    }
    if (regionGroup) {
      const opt = regionGroup.options.find((o) => o.id === selectedOptions[regionGroup.id]);
      if (opt) region = opt.label.toLowerCase();
    }
    const url = characterLogsUrl(gameLogs, { region, realmSlug, name: charName });
    if (url) setWclLink(url);
  }, [charName, selectedOptions, modalOpen, groupByKey, gameLogs]);

  // Scroll the focused section into view as the spotlight advances.
  useEffect(() => {
    if (mode !== "trial") return;
    const ref = cur === "characters" ? charSectionRef
      : cur === "availability" ? availSectionRef
      : (cur === "guild" || cur === "guildPrompt") ? guildSectionRef
      : cur === "guildInvite" ? guildDetailRef
      : cur === "plannerNav" ? plannerListRef
      : cur === "raid" ? raidSectionRef
      : cur === "planner" ? plannerSectionRef
      : null;
    if (ref?.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [step, mode, cur]);

  // === Replay mode: run on mount ===========================================
  useEffect(() => {
    if (mode !== "replay") return;
    clearReplayPending();
    let cancelled = false;
    (async () => {
      const result = await replayDraft((steps) => { if (!cancelled) setReplaySteps(steps); });
      if (cancelled) return;
      setReplaySteps(result.steps);
      setCreatedGuild(result.createdGuild);
      setReplayPhase(result.ok ? "done" : "failed");
    })();
    return () => { cancelled = true; };
  }, [mode]);

  // === draft persistence ===================================================
  const persist = (mutate: (d: TrialDraft) => void) => {
    const draft = loadDraft() || newDraft(gameId);
    draft.gameId = gameId;
    mutate(draft);
    saveDraft(draft);
  };

  const optionsToArray = (opts: Record<number, number | number[]>) =>
    Object.entries(opts).flatMap(([gid, val]) =>
      Array.isArray(val) ? val.map((v) => ({ group_id: Number(gid), option_id: v })) : [{ group_id: Number(gid), option_id: val }]
    );

  const buildCharacterPayload = (): CharacterPayload => {
    const options: Record<string, number | number[]> = {};
    for (const [gid, val] of Object.entries(selectedOptions)) {
      if (Array.isArray(val)) { if (val.length) options[gid] = val; }
      else if (val > 0) options[gid] = val;
    }
    return { gameId, name: charName.trim(), options, metadata: { wcl_url: wclLink.trim() || null } };
  };

  const buildGuildPayload = (): GuildPayload => ({
    gameId,
    name: gName.trim(),
    tag: gTag.trim(),
    region: gRegion || null,
    serverName: gRealm || null,
    faction: formatFaction(gFaction),
    metadata: {},
  });

  const submitGuild = () => {
    if (!gName.trim()) { setGuildError("Guild Name is required."); return; }
    if (!gTag.trim()) { setGuildError("Guild Tag is required."); return; }
    setGuildError(null);
    persist((d) => { d.guild = buildGuildPayload(); d.branch = "createGuild"; });
    setGuildCreated(true);
    setGuildModalOpen(false);
  };

  // Realm options filtered by the selected region (mirrors GuildsPage).
  const guildRegionOpts = groupByKey("region")?.options || [];
  const guildFactionOpts = groupByKey("faction")?.options || [];
  const guildSelectedRegionKey = guildRegionOpts.find((o) => o.label === gRegion)?.key
    || guildRegionOpts.find((o) => o.label === gRegion)?.label?.toLowerCase() || null;
  const guildRealmOpts = guildSelectedRegionKey
    ? (groupByKey("realm")?.options || []).filter((o) => { const a = o.metadata?.allowed_region_keys; return !a || a.includes(guildSelectedRegionKey); })
    : [];

  const validateCharacter = (): string | null => {
    const name = charName.trim();
    if (name.length < 2) return "Character name must be at least 2 characters";
    if (name.length > 24) return "Character name must be 24 characters or less";
    const requiredKeys = ["class", "spec", "realm", "faction", "region"];
    const missing: string[] = [];
    for (const group of groups) {
      if (requiredKeys.includes(group.key)) {
        const v = selectedOptions[group.id];
        const has = Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && Number(v) > 0;
        if (!has) missing.push(group.label || group.key);
      }
    }
    return missing.length ? `Please select: ${missing.join(", ")}` : null;
  };

  const submitCharacter = () => {
    const err = validateCharacter();
    if (err) { setCharError(err); return; }
    setCharError(null);
    persist((d) => { d.character = buildCharacterPayload(); });
    setCreatedChar({
      id: `trial_${Date.now()}`,
      name: charName.trim(),
      gameId, game_id: gameId,
      metadata: { wcl_url: wclLink.trim() || null },
      options: optionsToArray(selectedOptions),
    });
    setModalOpen(false);
  };

  const onAvailabilityChange = (next: boolean[]) => {
    setAvailSlots(next);
    persist((d) => { d.availability = { slots: boolArrayToBitstring(next), weekStart }; });
  };

  // Commit on signup. `includeGuild=false` drops the guild from the draft so only
  // the character (+ availability) is replayed. Nothing is written until here.
  const onKeep = (includeGuild: boolean) => {
    if (!includeGuild) persist((d) => { d.guild = null; });
    markReplayPending();
    window.location.href = `${API_URL}/auth/discord`;
  };
  const onStartOver = () => { clearDraft(); onExit(); };

  // Display props for the real RosterCharacterTile, derived from selections.
  const tile = useMemo(() => {
    if (!createdChar) return null;
    const classG = groupByKey("class");
    const classOpt = classG?.options.find((o) => o.id === selectedOptions[classG.id]);
    const classLabel = classOpt?.label || "Unknown";
    const classColor = classOpt?.metadata?.color || stringToColor(classLabel);
    const realmG = groupByKey("realm");
    const realmOpt = realmG?.options.find((o) => o.id === selectedOptions[realmG.id]);
    const specG = groupByKey("spec");
    const specVal = specG ? selectedOptions[specG.id] : undefined;
    const specOpt = specG?.options.find((o) => (Array.isArray(specVal) ? specVal.includes(o.id) : o.id === specVal));
    return { classLabel, classColor, realmLabel: realmOpt?.label || "Unknown Realm", specLabel: specOpt?.label || null };
  }, [createdChar, selectedOptions, groupByKey]);

  // =========================================================================
  // REPLAY MODE
  // =========================================================================
  if (mode === "replay") {
    const inviteCode = createdGuild?.invite_code || createdGuild?.inviteCode || null;
    if (replayPhase === "replaying") {
      return (
        <Overlay center>
          <div className="w-full max-w-md text-center">
            <Coach pose="confident"><span className="font-bold text-white">Locking in everything you built…</span> one sec.</Coach>
            <div className="mt-6 space-y-2 text-left">
              {replaySteps.map((s) => (
                <div key={s.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03]">
                  <StepIcon status={s.status} /><span className="text-sm text-white/80">{s.label}</span>
                </div>
              ))}
              {!replaySteps.length && <div className="text-white/40 text-sm animate-pulse">Setting things up…</div>}
            </div>
          </div>
        </Overlay>
      );
    }
    if (replayPhase === "failed") {
      const failed = replaySteps.find((s) => s.status === "failed");
      return (
        <Overlay center>
          <div className="w-full max-w-md">
            <Coach pose="think"><span className="font-bold text-white">Almost there.</span> Most of it went through. One step needs a retry inside the app.</Coach>
            <div className="mt-5 space-y-2">
              {replaySteps.map((s) => (
                <div key={s.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03]">
                  <StepIcon status={s.status} /><span className="text-sm text-white/80 flex-1">{s.label}</span>
                  {s.status === "failed" && s.error && <span className="text-[11px] text-red-300/80">{s.error}</span>}
                </div>
              ))}
            </div>
            {failed && <p className="mt-4 text-xs text-white/40">You can finish that from your roster. Nothing else was lost.</p>}
            <div className="mt-5 flex justify-end"><PrimaryBtn onClick={() => onComplete(createdGuild)}>Continue to Rallify</PrimaryBtn></div>
          </div>
        </Overlay>
      );
    }
    // success → Discord explainer
    return (
      <Overlay center>
        <div className="w-full max-w-lg">
          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 mb-3">
              <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-xl font-black text-white">You're all set!</h2>
            <p className="text-sm text-white/50 mt-1">Your character and availability are saved to your account.</p>
          </div>
          <Coach pose="present">
            <span className="font-bold text-white">Pro tip:</span> Rallify can DM you on Discord each reset to grab your availability and post your raids automatically, with no chasing signups.
          </Coach>
          <div className="mt-5 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/[0.07] p-5">
            <div className="text-sm font-bold text-white mb-2">{createdGuild ? "Connect your guild's Discord" : "Set up Discord reminders"}</div>
            <ol className="space-y-2 text-xs text-white/60 list-decimal list-inside">
              {createdGuild ? (
                <>
                  <li>Open your guild → <span className="text-white/80 font-medium">Settings → Discord</span> and invite the bot.</li>
                  <li>Pick the channel for availability prompts; members get a DM each reset.</li>
                </>
              ) : (
                <>
                  <li>Head to your <span className="text-white/80 font-medium">Roster</span>. Your character and times are there.</li>
                  <li>Join or create a guild to unlock Discord availability reminders.</li>
                </>
              )}
            </ol>
            {inviteCode && (
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="text-[10px] uppercase font-bold text-white/40 tracking-widest mb-1">Your guild invite code</div>
                <code className="px-2.5 py-1 rounded bg-black/50 border border-white/10 text-sm text-cyan-300 font-mono">{inviteCode}</code>
              </div>
            )}
          </div>
          <div className="mt-5 flex justify-end"><PrimaryBtn onClick={() => { clearDraft(); onComplete(createdGuild); }}>Go to my roster</PrimaryBtn></div>
        </div>
      </Overlay>
    );
  }

  // =========================================================================
  // TRIAL MODE: the Roster stage + step-locked spotlight
  // =========================================================================
  // Which page the stage shows. The keep step shows the furthest thing they built.
  const screen: "roster" | "guild" | "guildDetail" | "plannerList" | "createRaid" | "planner" =
    cur === "guildPrompt" || cur === "guild" ? "guild"
    : cur === "guildInvite" ? "guildDetail"
    : cur === "plannerNav" ? "plannerList"
    : cur === "raid" ? "createRaid"
    : cur === "planner" ? "planner"
    : cur === "keep" ? (raidBuilt ? "planner" : guildCreated ? "guild" : "roster")
    : "roster";
  // Dim is active on every guided step (not intro/keep) unless a modal owns
  // the screen or the player is mid-drag (the whole board must be readable
  // while choosing a seat). With a spotlight target it renders as a hole-dim;
  // without one it falls back to a plain full dim.
  const dimActive =
    (cur === "characters" || cur === "availability" || cur === "guildPrompt" || cur === "guild" || cur === "guildInvite" || cur === "plannerNav" || cur === "raid" || cur === "planner")
    && !modalOpen && !guildModalOpen && !suggestionsOpen && !dragSpec;
  // Journey progress shown above the coach: milestone n of 9 with a label, so
  // the player always knows where they are on the road to a published raid.
  const progress = (() => {
    const total = 9;
    switch (cur) {
      case "characters": return { n: 1, total, label: "Your character" };
      case "availability": return { n: 2, total, label: "Raid hours" };
      case "guildPrompt": case "guild": return { n: 3, total, label: "Your guild" };
      case "guildInvite": return { n: 4, total, label: "Recruiting" };
      case "plannerNav": return { n: 5, total, label: "The planner" };
      case "raid": return raidTab === 0 ? { n: 6, total, label: "Schedule" } : raidTab === 1 ? { n: 7, total, label: "Roster" } : { n: 8, total, label: "Publish" };
      case "planner": return { n: 9, total, label: "Raid live" };
      default: return null;
    }
  })();

  const raidOptions = groupByKey("raid_interest")?.options || [];
  const raidName = raidOptions.find((o) => o.id === raidOptId)?.label || raidLabel || "New Raid";
  const raidSize = 10;
  // Faked "suggested times" computed from (pretend) guild availability (of 23 members).
  const suggestedTimes = [
    { day: "Fri", time: "20:00", available: 11, total: 23, pct: 48 },
    { day: "Fri", time: "20:30", available: 11, total: 23, pct: 48 },
    { day: "Sat", time: "20:00", available: 10, total: 23, pct: 43 },
    { day: "Sat", time: "20:30", available: 10, total: 23, pct: 43 },
    { day: "Fri", time: "19:00", available: 7, total: 23, pct: 30 },
  ];
  // Real dates for the suggestions: reset week Wednesday + weekday offset.
  const RESET_DAY_OFFSET: Record<string, number> = { Wed: 0, Thu: 1, Fri: 2, Sat: 3, Sun: 4, Mon: 5, Tue: 6 };
  const suggestWeekWed = (() => { const d = new Date(getCurrentWednesday() + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + suggestWeek * 7); return d; })();
  const dateForSuggestion = (dayName: string) => {
    const d = new Date(suggestWeekWed);
    d.setUTCDate(d.getUTCDate() + (RESET_DAY_OFFSET[dayName] ?? 0));
    return d.toISOString().split("T")[0];
  };
  const suggestWeekLabel = (() => {
    const end = new Date(suggestWeekWed); end.setUTCDate(end.getUTCDate() + 6);
    const fmt = (dt: Date) => `${dt.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} ${dt.getUTCDate()}`;
    return `${fmt(suggestWeekWed)} – ${fmt(end)}`;
  })();
  const s0 = sessions[0];
  const updateSession = (idx: number, field: "date" | "start" | "end", val: string) =>
    setSessions((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: val } : s)));
  const bumpSession = (idx: number, field: "start" | "end", delta: number) =>
    setSessions((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: bumpHalfHour(s[field], delta) } : s)));
  const toggleSeriesDay = (day: string) =>
    setRecurring((prev) => { const n = { ...prev }; if (n[day]) delete n[day]; else n[day] = { start: "20:00", end: "23:00" }; return n; });
  // Planner detail header bits (session chips + short timezone label like
  // PlannerPage's formatRaidTime / formatIanaTzShortLabel).
  const sessionChips = scheduleMode === "series"
    ? Object.entries(recurring).map(([d, p]) => `${d} @ ${p.start}`)
    : sessions.filter((s) => s.date).map((s) => `${new Date(s.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} @ ${s.start}`);
  const tzShort = (() => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "short" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || timezone;
    } catch { return timezone; }
  })();
  // Directory guilds shown in the Guilds stage (search + My Guilds mirror GuildsPage).
  const ownGuildRow = guildCreated ? [{ id: "trial_own", name: gName, tag: gTag, faction: formatFaction(gFaction), serverName: gRealm, region: gRegion, memberCount: 1, isOwner: true, isYours: true }] : [];
  const dirRows = [...ownGuildRow, ...directory.map((g) => ({ ...g, isYours: false }))];
  const filteredGuilds = dirRows.filter((g) => {
    if (myGuildsOnly && !g.isYours) return false;
    const q = guildSearch.trim().toLowerCase();
    if (!q) return true;
    return (g.name || "").toLowerCase().includes(q) || (g.tag || "").toLowerCase().includes(q) || (g.serverName || "").toLowerCase().includes(q);
  });
  // The player's role for the mock roster (derived from their chosen spec).
  const playerRole = (() => {
    const specG = groupByKey("spec");
    const specVal = specG ? selectedOptions[specG.id] : undefined;
    const specOpt = specG?.options.find((o) => (Array.isArray(specVal) ? specVal.includes(o.id) : o.id === specVal));
    const rk = specOpt?.metadata?.role_key;
    return rk === "tank" ? "Tank" : rk === "healer" ? "Healer" : rk === "melee_dps" ? "Melee DPS" : rk === "ranged_dps" ? "Ranged DPS" : "Melee DPS";
  })();
  const playerSelf = { name: createdChar?.name || charName || "You", cls: tile?.classLabel || "Warrior", role: playerRole, spec: tile?.specLabel || "", you: true };

  // --- Roster drag-and-drop (Roster tab) -----------------------------------
  const filledSeats = seats.filter(Boolean).length;
  const ROLE_TABS = ["Tank", "Healer", "Melee DPS", "Ranged DPS"];
  const seatPlayer = (s: any) => s?.player || s?.name;
  const seatedPlayers = new Set(seats.filter(Boolean).map(seatPlayer));
  // Available vs already-seated pool players (mirrors CreateRaidPage's
  // "Already Seated" section: one seat per player, alts gray out).
  const availablePool = MOCK_POOL.filter((p) => !seatedPlayers.has(p.name));
  const seatedPool = MOCK_POOL.filter((p) => seatedPlayers.has(p.name));
  const poolCount = (role: string | null) => availablePool.filter((p) => !role || p.specs.some((s) => s.role === role)).length;
  const visiblePool = availablePool.filter((p) => !poolFilter || p.specs.some((s) => s.role === poolFilter));
  const seatSpec = (idx: number, entry: any) =>
    setSeats((prev) => { const n = [...prev]; n[idx] = { ...entry }; return n; });
  const seatFirstEmpty = (entry: any) => {
    const idx = seats.indexOf(null);
    if (idx === -1) return;
    seatSpec(idx, entry);
  };
  const assignSeat = (idx: number) => {
    if (!dragSpec) return;
    seatSpec(idx, dragSpec);
    setDragSpec(null);
  };
  const clearSeat = (idx: number) => setSeats((prev) => { const n = [...prev]; n[idx] = null; return n; });
  const addToBench = () => { if (!dragSpec) return; setBench((b) => [...b, { ...dragSpec }]); setDragSpec(null); };
  const comp: Record<string, number> = {
    Tank: seats.filter((s) => s?.role === "Tank").length,
    Healer: seats.filter((s) => s?.role === "Healer").length,
    "Melee DPS": seats.filter((s) => s?.role === "Melee DPS").length,
    "Ranged DPS": seats.filter((s) => s?.role === "Ranged DPS").length,
  };
  const seatedClassCounts = (() => {
    const counts: Record<string, number> = {};
    seats.forEach((s) => { if (s?.cls) counts[s.cls] = (counts[s.cls] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();
  // Composition Coverage (mock buffs/debuffs; covered = seated class provides it)
  const seatedClasses = new Set(seats.filter(Boolean).map((s: any) => s.cls));
  const coverage = TRIAL_COVERAGE.map((g) => ({
    ...g,
    tags: g.tags.map((t) => ({ ...t, covered: t.providers.some((p) => seatedClasses.has(p)) })),
  })).map((g) => ({ ...g, coveredCount: g.tags.filter((t) => t.covered).length }));
  const coverageCovered = coverage.reduce((s, g) => s + g.coveredCount, 0);
  const coverageTotal = coverage.reduce((s, g) => s + g.tags.length, 0);
  const coveragePct = coverageTotal ? Math.round((coverageCovered / coverageTotal) * 100) : 0;
  const seatedScores = seats.filter((s: any) => s && typeof s.score === "number").map((s: any) => s.score);
  const avgParse = seatedScores.length ? Math.round(seatedScores.reduce((a: number, b: number) => a + b, 0) / seatedScores.length) : null;
  // On "Create Raid", fill any empty seats so the planner looks populated.
  const autoFillSeats = () => setSeats((prev) => {
    const n = [...prev];
    const placed = new Set(n.filter(Boolean).map(seatPlayer));
    const fillers = [playerSelf, ...MOCK_RAIDERS];
    let fi = 0;
    for (let i = 0; i < n.length; i++) {
      if (n[i]) continue;
      while (fi < fillers.length && placed.has(seatPlayer(fillers[fi]))) fi++;
      if (fi >= fillers.length) break;
      n[i] = fillers[fi]; placed.add(seatPlayer(fillers[fi])); fi++;
    }
    return n;
  });
  const goRaidAdvance = () => {
    if (raidTab < 2) { setRaidTab(raidTab + 1); return; }
    const optId = raidOptId || raidOptions[0]?.id || "";
    if (optId) setRaidOptId(optId);
    autoFillSeats();
    setRaidBuilt(true);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const goRaidBack = () => { if (raidTab > 0) setRaidTab(raidTab - 1); else setStep((s) => Math.max(0, s - 1)); };

  // --- App-shell nav behavior ------------------------------------------------
  // The nav highlights the tab that matches the current tour stage. Any other
  // tab pops a notice: account-gated tabs say what unlocks after signup, tour
  // tabs point back at the guide. Nobody navigates away mid-tour.
  const activeNavTab: string = screen === "roster" ? "Roster" : screen === "guild" ? "Guilds" : "Planner";
  // After the invite-code explainer the tour asks the player to click Planner
  // in the nav for real; that click advances the tour instead of a notice.
  const awaitingPlannerNav = cur === "guildInvite" && invitePhase === "nav";
  const showNotice = (key: string) => {
    setNavNotice(key);
    if (navNoticeTimer.current) clearTimeout(navNoticeTimer.current);
    navNoticeTimer.current = setTimeout(() => setNavNotice(null), 6000);
  };
  const onNavClick = (t: string) => {
    if (t === "Planner" && awaitingPlannerNav) {
      setNavNotice(null);
      setStep(STEPS.indexOf("plannerNav"));
      return;
    }
    if (t === activeNavTab) { setNavNotice(null); return; }
    showNotice(t);
  };
  // Real "Sign in" button in the header. If they built anything, keep it: mark
  // the draft for replay so their work survives the Discord round-trip.
  const signInEarly = () => {
    if (loadDraft()) markReplayPending();
    window.location.href = `${API_URL}/auth/discord`;
  };

  return (
    <div
      className="fixed inset-0 z-[140] overflow-y-auto overflow-x-hidden text-white"
      style={{
        ["--c-surface" as any]: "rgba(16,21,31,0.88)",
        ["--c-surface-2" as any]: "rgba(18,24,36,0.78)",
        ["--c-edge" as any]: "rgba(255,255,255,0.08)",
        ["--c-glow" as any]: "rgba(39,197,245,0.35)",
        ["--c-ink" as any]: "rgba(255,255,255,0.82)",
        ["--c-pill" as any]: "linear-gradient(90deg, rgba(29,36,50,0.85), rgba(33,40,56,0.85))",
        ["--c-header" as any]: "linear-gradient(90deg, rgba(18,23,33,0.96) 0%, rgba(18,23,33,0.90) 35%, rgba(18,23,33,0.96) 100%)",
      } as React.CSSProperties}
    >
      {/* BACKGROUND (identical to App.tsx <Background />) */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(56,189,248,0.12),transparent),linear-gradient(to_bottom,rgba(10,14,22,1),rgba(6,10,18,1))]" />
      </div>

      {/* HEADER: replica of the real app shell (regular-user tabs). Kept above
          the spotlight dim so the nav is always visible and clickable. */}
      <header className="fixed top-0 left-0 right-0 z-[180] w-full">
        <div className="relative">
          <div className="h-20 w-full border-b border-white/5 backdrop-blur-2xl shadow-2xl shadow-black/50 transition-all duration-300" style={{ background: "rgba(10,12,16,0.85)" }}>
            <div className="mx-auto px-6 lg:px-10 h-full flex items-center justify-between relative">
              {/* Left Side: Logo */}
              <div className="flex items-center gap-3 group cursor-pointer" onClick={() => onNavClick("Home")}>
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 ring-1 ring-cyan-400/30 flex items-center justify-center font-black tracking-tight text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.2)] group-hover:scale-105 transition-transform">
                  <span className="text-lg drop-shadow-md">R</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold tracking-wide text-white leading-none">RALLIFY</span>
                  <span className="text-[10px] text-white/30 font-medium tracking-wider uppercase group-hover:text-cyan-400 transition-colors">Less Searching, More Playing</span>
                </div>
              </div>

              {/* Center: Nav - Floating Pill */}
              <nav className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 p-1.5 rounded-full border border-white/10 bg-black/20 backdrop-blur-md shadow-inner">
                {NAV_TABS.map((t) => (
                  <ShellTabButton key={t} label={t === "Roster" ? "Profile" : t} icon={navIcon(t)} active={activeNavTab === t} attention={t === "Planner" && awaitingPlannerNav} tt={t === "Planner" ? "nav-planner" : undefined} onClick={() => onNavClick(t)} />
                ))}
              </nav>

              {/* Right Side: Auth */}
              <div className="flex items-center gap-3">
                <button onClick={signInEarly} className="rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10">Sign in</button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* LOCKED-TAB NOTICE */}
      {navNotice && NAV_NOTICES[navNotice] && (
        <div className="fixed top-[88px] left-1/2 -translate-x-1/2 z-[185] w-full max-w-md px-4">
          <div className="rounded-xl border border-cyan-500/25 bg-[#0c1017]/95 backdrop-blur-md shadow-2xl shadow-black/60 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="mt-0.5 h-7 w-7 shrink-0 rounded-lg bg-cyan-500/10 border border-cyan-400/25 flex items-center justify-center">
              {NAV_NOTICES[navNotice].locked ? (
                <svg className="w-3.5 h-3.5 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              ) : (
                <svg className="w-3.5 h-3.5 text-cyan-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-white">{NAV_NOTICES[navNotice].title}</div>
              <p className="text-[11px] text-white/50 leading-relaxed mt-0.5">{NAV_NOTICES[navNotice].body}</p>
              {NAV_NOTICES[navNotice].locked && (
                <button onClick={signInEarly} className="mt-2 text-[11px] font-bold text-cyan-300 hover:text-cyan-200 transition-colors">Sign up free with Discord →</button>
              )}
            </div>
            <button onClick={() => setNavNotice(null)} className="text-white/30 hover:text-white text-xs shrink-0">✕</button>
          </div>
        </div>
      )}

      {/* LAYOUT (same structure as App.tsx: pt-20 for the fixed header) */}
      <div className="relative pt-20 min-h-screen">
        {/* Trial banner: sits in the app's banner slot under the header, above
            the spotlight dim so Exit is always reachable. */}
        <div className="relative z-[160] bg-cyan-500/10 border-b border-cyan-500/20 px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-xs text-cyan-100/80"><strong className="text-cyan-100 font-bold">Guided tour.</strong> This is a live preview, nothing is saved until you sign up.</span>
            </div>
            <button onClick={onExit} className="text-[11px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors">Exit tour</button>
          </div>
        </div>

        {/* MAIN CONTENT (same layout as App.tsx) */}
        <main className="p-5 lg:p-8">
          {/* Stage: mirrors whichever real page the current step maps to. */}
          <div className="max-w-7xl mx-auto space-y-8 pb-40">
        {screen === "roster" ? (
        <>
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/10 pb-6">
          <div>
            <h1 className="text-3xl font-black text-white tracking-tight uppercase">Your Characters</h1>
            <p className="text-white/50 text-sm mt-1">Manage characters and availability.</p>
          </div>
        </header>

        {/* CHARACTERS */}
        <section ref={charSectionRef} className="scroll-mt-24">
          <div className="flex items-center justify-between mb-6 p-4 bg-emerald-950/30 border border-emerald-500/15 rounded-xl">
            <div className="flex items-center gap-2 text-sm font-bold text-emerald-300/80">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
              This is your roster. Every character you play lives here.
            </div>
            <button data-tt="new-char" onClick={() => { setCharError(null); setModalOpen(true); if (games.length && !gameId) setGameId(games[0].id); }}
              className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold uppercase tracking-wide rounded shadow-lg shadow-cyan-900/20 transition-all">
              + New Character
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {createdChar && tile ? (
              <div data-tt="char-card">
              <RosterCharacterTile
                char={createdChar}
                classLabel={tile.classLabel}
                classColor={tile.classColor}
                roleOpts={[]}
                realmLabel={tile.realmLabel}
                wclPerf={null}
                perfScore={null}
                perfColor={null}
                heat={null}
                avatarImage={undefined}
                isRefreshing={false}
                onEdit={() => setModalOpen(true)}
                onRefresh={() => {}}
                onBis={() => {}}
                onGuildOpen={() => {}}
                onDelete={() => { setCreatedChar(null); persist((d) => { d.character = null; }); }}
              />
              </div>
            ) : (
              <button onClick={() => { setCharError(null); setModalOpen(true); if (games.length && !gameId) setGameId(games[0].id); }}
                className="flex flex-col items-center justify-center gap-3 h-60 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] text-white/40 hover:border-cyan-400/40 hover:text-cyan-300 transition-all">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                <span className="text-xs font-bold uppercase tracking-widest">Add your first character</span>
              </button>
            )}
          </div>
        </section>

        {/* AVAILABILITY */}
        <section ref={availSectionRef} id="availability" className="scroll-mt-24 pt-8 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white uppercase flex items-center gap-2">
              <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Your Availability
            </h2>
          </div>
          <div data-tt="avail-grid" className="bg-[#0e121b] p-5 rounded-xl border border-white/15 shadow-inner">
            <div className="mb-3 flex justify-end">
              <span className="inline-flex items-center gap-2 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-200" title={timezone}>
                <span>Timezone:</span><span>{timezone}</span>
              </span>
            </div>
            <AvailabilityGrid
              week={availSlots}
              weekStartIso={weekStart}
              userTimezone={timezone}
              displayDayOrderLocalMon0={[2, 3, 4, 5, 6, 0, 1]}
              daysDisplay={["Wed", "Thu", "Fri", "Sat", "Sun", "Mon", "Tue"]}
              onChange={onAvailabilityChange}
            />
            <p className="mt-3 text-xs text-white/40">Week starts Wed to match raid reset. Drag across the grid to add a session.</p>
          </div>
        </section>
        </>
        ) : screen === "guild" ? (
        /* GUILDS stage (identical to GuildsPage list view) */
        <section ref={guildSectionRef} className="scroll-mt-24">
          <div className="max-w-7xl mx-auto space-y-6 relative">
          {/* 1. HEADER */}
          <div className="flex items-end justify-between border-b border-white/10 pb-6">
            <div>
              <div>
                <h1 className="text-3xl font-black text-white tracking-tight uppercase">GUILD DIRECTORY</h1>
              </div>
            </div>
            <div className="flex gap-3">
              {/* SEARCH INPUT */}
              <div className="relative group mr-2">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-cyan-400 transition-colors pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={guildSearch}
                  onChange={(e) => setGuildSearch(e.target.value)}
                  placeholder="Find guild..."
                  className="bg-black/30 border border-white/10 rounded-full pl-8 pr-4 py-1.5 text-xs text-white focus:border-cyan-500/50 focus:bg-black/50 outline-none w-48 transition-all"
                />
              </div>

              <div className="flex items-center gap-4 mr-4 border-l border-white/10 pl-4">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${myGuildsOnly ? "bg-cyan-500 border-cyan-500" : "border-white/30 bg-transparent group-hover:border-white/50"}`}>
                    {myGuildsOnly && <div className="w-2 h-2 bg-white rounded-sm" />}
                  </div>
                  <input type="checkbox" className="hidden" checked={myGuildsOnly} onChange={(e) => setMyGuildsOnly(e.target.checked)} />
                  <span className={`text-xs font-bold uppercase tracking-wide transition-colors ${myGuildsOnly ? "text-white" : "text-white/50 group-hover:text-white/80"}`}>
                    My Guilds
                  </span>
                </label>
              </div>
              {!guildCreated && (
                <button
                  data-tt="create-guild"
                  onClick={() => { setGuildError(null); setGuildModalOpen(true); }}
                  className="flex items-center gap-2 px-4 py-1.5 bg-white text-black text-xs font-bold rounded-full hover:bg-gray-200 transition-colors shadow-lg shadow-white/10"
                >
                  <span>+ Create Guild</span>
                </button>
              )}
            </div>
          </div>

          {/* 2. MAIN CONTENT AREA (list view) */}
          <div className="space-y-6">
            {/* Join Bar */}
            <div className="p-4 bg-emerald-950/40 border border-emerald-500/20 rounded-xl flex items-center gap-4">
              <div className="flex items-center gap-2 ml-1">
                <svg className="w-5 h-5 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>
                <span className="text-sm font-bold text-emerald-300">Join a guild?</span>
                <div className="relative group">
                  <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400/70 text-[10px] font-bold flex items-center justify-center cursor-help hover:bg-emerald-500/30 hover:text-emerald-300 transition-colors">?</span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-black/95 border border-white/20 rounded-lg text-xs text-white/80 whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-xl">
                    Guild leaders can generate invite codes from their guild settings
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/95"></div>
                  </div>
                </div>
              </div>
              <input
                value={joinCode} onChange={(e) => { setJoinCode(e.target.value); setJoinNote(false); }} placeholder="Enter invite code..."
                className="bg-black/50 border border-emerald-500/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 w-40 transition-colors"
              />
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-white/30 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
                <select
                  value={joinCharId} onChange={(e) => setJoinCharId(e.target.value)}
                  className="bg-black/50 border border-emerald-500/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 w-52 transition-colors"
                >
                  <option value="">Select Char...</option>
                  {createdChar && <option value={createdChar.id}>{createdChar.name}</option>}
                </select>
              </div>
              <button onClick={() => setJoinNote(true)} disabled={!joinCode || !joinCharId} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5 shadow-lg shadow-emerald-900/30">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
                Join
              </button>
            </div>
            {joinNote && (
              <div className="text-[11px] text-emerald-300/70 px-1 -mt-3">Preview: joining with a real invite code works as soon as you sign up.</div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredGuilds.map((g: any) => (
                <div key={g.id} data-tt={g.isYours ? "own-guild-card" : undefined}>
                  <TrialGuildCard g={g} isYours={g.isYours} isOwner={g.isOwner} onClick={g.isYours ? () => setStep(STEPS.indexOf("guildInvite")) : undefined} />
                </div>
              ))}
              {filteredGuilds.length === 0 && (
                <div className="col-span-full py-20 text-center border border-dashed border-white/10 rounded-xl">
                  <div className="text-4xl mb-2">🔮</div>
                  <div className="text-white/40 text-sm font-medium">No guilds found matching your criteria.</div>
                </div>
              )}
            </div>
          </div>
          </div>
        </section>
        ) : screen === "guildDetail" ? (
        /* GUILD DETAIL stage (mirrors GuildsPage detail view: hero + Recruit panel) */
        <section ref={guildDetailRef} className="scroll-mt-24">
          <div className="max-w-7xl mx-auto space-y-6 relative">
            {/* Header with breadcrumb */}
            <div className="flex items-end justify-between border-b border-white/10 pb-6">
              <div className="flex flex-col gap-1">
                <button onClick={() => setStep(STEPS.indexOf("guild"))} className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white transition-colors flex items-center gap-1 self-start">
                  &larr; Back to Directory
                </button>
                <h1 className="text-3xl font-black text-white tracking-tight uppercase flex items-center gap-3">
                  {gName}
                  {gTag && <span className="text-sm font-mono text-white/30 bg-white/5 px-2 py-0.5 rounded">[{gTag}]</span>}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-black/30 rounded-lg border border-white/10 p-0.5">
                  <button className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all bg-white/10 text-white shadow">Guild Info</button>
                  <button onClick={() => showNotice("GUILDTOOLS")} className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all text-white/35 hover:text-white/60">
                    <span className="flex items-center gap-1.5">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                      Dashboard
                    </span>
                  </button>
                </div>
                <button onClick={() => showNotice("GUILDTOOLS")} className="flex items-center gap-2 px-3 py-1.5 border border-white/20 bg-white/5 text-white text-xs font-bold rounded-lg hover:bg-white/10 transition-colors">
                  <span>Settings</span>
                </button>
              </div>
            </div>

            {/* HERO BANNER + COMMAND CENTER */}
            <div className="relative w-full rounded-xl overflow-hidden mb-8 border border-white/10">
              <div className={`absolute inset-0 bg-gradient-to-r ${factionGradient(gFaction)} opacity-50`} />
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
              <div className="relative p-6 lg:p-8 flex flex-col lg:flex-row gap-6 items-start">
                <div className="w-28 h-28 rounded-2xl bg-[#09090b] border-4 border-[#09090b] shadow-2xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <span className="text-4xl font-bold text-white/20">{(gName || "G").charAt(0)}</span>
                </div>
                <div className="flex-1 flex flex-col sm:flex-row gap-3 min-w-0 w-full lg:w-auto">
                  {/* Recruit Panel */}
                  <div data-tt="recruit-panel" className="flex-1 p-4 rounded-xl bg-black/50 backdrop-blur-sm border border-emerald-500/15 space-y-3">
                    <h3 className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Recruit</h3>
                    <div className="flex gap-2">
                      <code className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[10px] text-white/60 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                        {`${window.location.origin}/#Invite?code=${TRIAL_INVITE_CODE}`}
                      </code>
                      <button
                        onClick={() => { setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2000); }}
                        className={`px-2 py-1 border rounded text-[10px] font-bold transition-all whitespace-nowrap ${inviteCopied ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
                      >
                        {inviteCopied ? "✓" : "Copy"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                      <span className="text-[9px] text-white/25 uppercase tracking-wider">Code</span>
                      <code className="text-[11px] text-white/60 font-mono tracking-widest">{TRIAL_INVITE_CODE}</code>
                      <button
                        onClick={() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }}
                        className={`px-1.5 py-0.5 border rounded text-[9px] font-bold transition-all ${codeCopied ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-300" : "bg-white/5 border-white/10 text-white/50 hover:bg-white/10"}`}
                      >
                        {codeCopied ? "✓" : "Copy"}
                      </button>
                    </div>
                    <p className="text-[9px] text-white/25">Sample code. Yours is generated when you sign up.</p>
                  </div>
                  {/* Discord Panel */}
                  <div className="flex-1 p-4 rounded-xl bg-black/50 backdrop-blur-sm border border-[#5865F2]/20 space-y-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z" /></svg>
                      <h3 className="text-[10px] font-bold text-[#5865F2] uppercase tracking-widest">Discord</h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <button onClick={() => showNotice("DISCORD")} className="flex-1 px-3 py-1.5 bg-[#5865F2] hover:bg-[#4752C4] text-white text-[10px] font-bold rounded-lg text-center transition-colors">Add to Discord</button>
                        <button onClick={() => showNotice("DISCORD")} className="px-2 py-1.5 border rounded-lg text-[10px] font-bold transition-all bg-white/5 border-white/10 text-white/70 hover:bg-white/10">Copy</button>
                      </div>
                      <p className="text-[10px] text-white/30">The bot DMs members each reset to collect availability and posts your raids.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Roster strip */}
            <div>
              <h2 className="text-[10px] uppercase font-bold text-white/40 tracking-widest mb-3">Roster · 1 character</h2>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.03] max-w-md">
                <div className="w-10 h-10 rounded-lg bg-[#15161d] border border-white/10 flex items-center justify-center text-sm font-bold" style={{ color: tile?.classColor || "#fff" }}>{(createdChar?.name || "?").charAt(0)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{createdChar?.name || "Your character"}</div>
                  <div className="text-[11px] text-white/40">{[tile?.classLabel, tile?.specLabel].filter(Boolean).join(" · ")}</div>
                </div>
                <span className="text-[9px] font-bold uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded">Guild Master</span>
              </div>
            </div>
          </div>
        </section>
        ) : screen === "plannerList" ? (
        /* PLANNER LIST stage (identical to PlannerPage's list view) */
        <section ref={plannerListRef} className="scroll-mt-24">
          <div className="max-w-5xl mx-auto space-y-8">
            {/* HERO SECTION */}
            <section>
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0F1115] shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-black pointer-events-none" />
                <div className="relative z-10 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="space-y-2">
                    <h2 className="text-3xl font-black text-white tracking-tight">RAID PLANNER</h2>
                    <p className="text-sm text-white/50 max-w-lg font-medium leading-relaxed">Coordinate raid sign-ups and optimize your roster</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-tt="schedule-raid"
                      onClick={() => setStep(STEPS.indexOf("raid"))}
                      className="group relative inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-indigo-500 hover:shadow-[0_0_20px_rgba(99,102,241,0.4)]"
                    >
                      <span>+ Schedule Raid</span>
                    </button>
                    <button
                      onClick={() => showNotice("ARC")}
                      className="group relative inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-all hover:from-amber-500 hover:to-orange-500 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span>Auto Schedule</span>
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* SCHEDULE LIST */}
            <section className="space-y-3">
              <div className="flex items-center gap-2 mb-4 px-1">
                <span className="w-1 h-4 bg-cyan-500 rounded-sm shadow-[0_0_8px_rgba(6,182,212,0.6)]"></span>
                <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest">Upcoming Raids</h3>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/40 p-12 text-center">
                <div className="text-white/40 text-sm italic">No upcoming raids scheduled.</div>
              </div>
            </section>
          </div>
        </section>
        ) : screen === "createRaid" ? (
        /* CREATE RAID stage (identical to CreateRaidPage's 3-step wizard) */
        <section ref={raidSectionRef} className="scroll-mt-24">
          <div className="relative flex w-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-slate-900 via-slate-900 to-black shadow-xl h-[calc(100vh-160px)] animate-in fade-in zoom-in-95 duration-300">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-[-10%] left-[-10%] h-[500px] w-[500px] rounded-full bg-indigo-900/20 blur-[100px]" />
              <div className="absolute bottom-[-10%] right-[-10%] h-[500px] w-[500px] rounded-full bg-cyan-900/20 blur-[100px]" />
            </div>

            {/* HEADER */}
            <div className="relative z-10 flex shrink-0 flex-col gap-4 border-b border-white/10 bg-white/[0.02] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h2 className="text-xl font-bold tracking-tight text-white">
                    {scheduleMode === "series" ? "Create Recurring Series" : scheduleMode === "multi" ? "Create Multi-Day Operation" : "Create Raid"}
                  </h2>
                  <div className="text-xs text-white/50">Configure your raid and build your roster.</div>
                </div>
                <div className="flex items-center gap-1 bg-black/40 rounded-full p-1 border border-white/10">
                  {["Setup", "Roster", "Review"].map((l, i) => {
                    const active = i === raidTab; const done = i < raidTab;
                    return (
                      <button key={l} onClick={() => i < raidTab && setRaidTab(i)} className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${active ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 shadow-[0_0_15px_rgba(34,211,238,0.15)]" : done ? "text-emerald-400 hover:bg-white/5 cursor-pointer" : "text-white/20"}`}>
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${active ? "bg-cyan-500 text-black" : done ? "bg-emerald-500 text-black" : "bg-white/10 text-white/50"}`}>{i + 1}</span>
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-[2px] w-full bg-white/5">
                <div className="h-full bg-gradient-to-r from-cyan-500 via-emerald-500 to-cyan-500 transition-all duration-500 ease-out" style={{ width: `${((raidTab + 1) / 3) * 100}%` }} />
              </div>
            </div>

            {/* CONTENT */}
            <div className="relative z-10 flex-1 overflow-hidden">
              {raidTab === 0 && (
                <div className="h-full overflow-y-auto p-8">
                <div className="mx-auto max-w-5xl space-y-10">
                  <div className="space-y-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-white/40 pl-1">Guild</label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      <button className="group relative flex items-center gap-4 rounded-xl border p-4 text-left transition-all duration-200 border-cyan-500 bg-cyan-900/20 shadow-[0_0_25px_rgba(34,211,238,0.1)] ring-1 ring-cyan-500/50">
                        <div className="h-12 w-12 shrink-0 rounded-lg bg-black/60 flex items-center justify-center font-bold text-xl overflow-hidden border border-white/10 text-cyan-300">{(gName || "G").charAt(0).toUpperCase()}</div>
                        <div>
                          <div className="text-sm font-bold text-white">{gName || "Your Guild"}</div>
                          <div className="text-[11px] font-medium text-white/40">{gRegion || "EU"} — {gRealm || "Unknown"}</div>
                        </div>
                        <div className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-pulse" />
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-10 md:grid-cols-2">
                    <div className="space-y-4">
                      <label className="text-xs font-bold uppercase tracking-widest text-white/40 pl-1">Raid Details</label>
                      <div className="space-y-3">
                        <div className="relative">
                          <select value={raidOptId} onChange={(e) => { const v = Number(e.target.value); setRaidOptId(v); setChainedIds([]); const o = raidOptions.find((x) => x.id === v); if (o) setRaidLabel(o.label); }} className="w-full appearance-none rounded-xl border border-white/10 bg-black/40 px-4 py-3.5 text-sm text-white focus:border-cyan-500 focus:outline-none transition-colors hover:bg-black/60">
                            <option value="">Select Raid Template...</option>
                            {raidOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                          <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40">▼</div>
                        </div>

                        {/* Raid Chaining */}
                        {raidOptId && (
                          <div className="space-y-2">
                            {chainedIds.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {chainedIds.map((optId) => {
                                  const opt = raidOptions.find((o) => o.id === optId);
                                  return (
                                    <div key={optId} className="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-sm">
                                      <svg className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                      <span className="text-cyan-200 font-medium">{opt?.label || `Raid #${optId}`}</span>
                                      <button onClick={() => setChainedIds((prev) => prev.filter((id) => id !== optId))} className="text-cyan-400/60 hover:text-red-400 transition-colors ml-1">
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {chainedIds.length < 2 && raidOptions.filter((o) => o.id !== raidOptId && !chainedIds.includes(o.id)).length > 0 && (
                              <div className="relative">
                                <select
                                  value=""
                                  onChange={(e) => { const v = Number(e.target.value); if (v && !chainedIds.includes(v)) setChainedIds((prev) => [...prev, v]); }}
                                  className="w-full appearance-none rounded-lg border border-dashed border-cyan-500/30 bg-cyan-500/5 px-4 py-2.5 text-sm text-cyan-300 focus:border-cyan-500 focus:outline-none transition-colors hover:bg-cyan-500/10 cursor-pointer"
                                >
                                  <option value="" className="bg-slate-900 text-white/60">+ Link Another Raid...</option>
                                  {raidOptions.filter((o) => o.id !== raidOptId && !chainedIds.includes(o.id)).map((opt) => (
                                    <option key={opt.id} value={opt.id} className="bg-slate-900 text-white">{opt.label}</option>
                                  ))}
                                </select>
                                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400/50">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                </div>
                              </div>
                            )}
                            {chainedIds.length > 0 && (
                              <div className="text-[10px] text-cyan-300/60 pl-1">
                                Linked raids share one roster, one session, and one soft reserve pool.
                                Players locked to any raid in the chain are excluded.
                              </div>
                            )}
                          </div>
                        )}

                        <input value={raidLabel} onChange={(e) => setRaidLabel(e.target.value)} placeholder="Custom Label (e.g. 'Progression Night')" className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3.5 text-sm text-white placeholder:text-white/20 focus:border-cyan-500 focus:outline-none transition-colors hover:bg-black/60" />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between pl-1">
                        <label className="text-xs font-bold uppercase tracking-widest text-white/40">Schedule & Sessions</label>
                        <PickerTzLabel title="Date and times use this timezone. Submitted sessions are stored as UTC." />
                      </div>

                      {/* 3-WAY TAB SWITCHER */}
                      <div className="flex bg-black/40 border border-white/10 p-1 rounded-xl mb-3">
                        <button onClick={() => setScheduleMode("single")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${scheduleMode === "single" ? "bg-white/10 text-white shadow-sm border border-white/10" : "text-white/40 hover:text-white/60"}`}>Single Event</button>
                        <button onClick={() => setScheduleMode("multi")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${scheduleMode === "multi" ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/30" : "text-white/40 hover:text-white/60"}`}>Multi-Day</button>
                        <button onClick={() => setScheduleMode("series")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${scheduleMode === "series" ? "bg-indigo-500/20 text-indigo-200 border border-indigo-500/30" : "text-white/40 hover:text-white/60"}`}>Recurring Series</button>
                      </div>

                      <div className="space-y-3">
                        {scheduleMode === "single" && (
                          <div className="animate-in fade-in space-y-3">
                            <input style={{ colorScheme: "dark" }} type="date" value={s0.date} onChange={(e) => updateSession(0, "date", e.target.value)} className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3.5 text-sm text-white focus:border-cyan-500 focus:outline-none transition-colors hover:bg-black/60" />
                            <div className="flex gap-4">
                              <div className="flex flex-1 items-center gap-1 rounded-xl border border-white/10 bg-black/40 p-1.5">
                                <button onClick={() => bumpSession(0, "start", -30)} className="h-9 w-9 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 hover:text-white flex items-center justify-center">-</button>
                                <input style={{ colorScheme: "dark" }} type="time" value={s0.start} onChange={(e) => updateSession(0, "start", e.target.value)} className="flex-1 bg-transparent text-center text-sm font-bold text-white focus:outline-none" />
                                <button onClick={() => bumpSession(0, "start", 30)} className="h-9 w-9 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 hover:text-white flex items-center justify-center">+</button>
                              </div>
                              <span className="flex items-center text-white/20 font-bold">➕</span>
                              <div className="flex flex-1 items-center gap-1 rounded-xl border border-white/10 bg-black/40 p-1.5">
                                <button onClick={() => bumpSession(0, "end", -30)} className="h-9 w-9 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 hover:text-white flex items-center justify-center">-</button>
                                <input style={{ colorScheme: "dark" }} type="time" value={s0.end} onChange={(e) => updateSession(0, "end", e.target.value)} className="flex-1 bg-transparent text-center text-sm font-bold text-white focus:outline-none" />
                                <button onClick={() => bumpSession(0, "end", 30)} className="h-9 w-9 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 hover:text-white flex items-center justify-center">+</button>
                              </div>
                            </div>
                          </div>
                        )}

                        {scheduleMode === "multi" && (
                          <div className="animate-in fade-in space-y-3">
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                              {sessions.map((session, idx) => (
                                <div key={idx} className="grid grid-cols-[130px_1fr_auto_1fr_auto] items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-2">
                                  <input style={{ colorScheme: "dark" }} type="date" value={session.date} onChange={(e) => updateSession(idx, "date", e.target.value)} className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-cyan-500/50" />
                                  <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1 min-w-0">
                                    <button onClick={() => bumpSession(idx, "start", -30)} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">-</button>
                                    <input value={session.start} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-white focus:outline-none min-w-0" />
                                    <button onClick={() => bumpSession(idx, "start", 30)} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">+</button>
                                  </div>
                                  <span className="text-white/20 text-xs px-1">to</span>
                                  <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1 min-w-0">
                                    <button onClick={() => bumpSession(idx, "end", -30)} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">-</button>
                                    <input value={session.end} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-white focus:outline-none min-w-0" />
                                    <button onClick={() => bumpSession(idx, "end", 30)} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">+</button>
                                  </div>
                                  {sessions.length > 1 && (
                                    <button onClick={() => setSessions((prev) => prev.filter((_, i) => i !== idx))} className="h-6 w-6 flex items-center justify-center rounded text-white/20 hover:text-red-400 transition-colors">✕</button>
                                  )}
                                </div>
                              ))}
                            </div>
                            <button onClick={() => setSessions((prev) => [...prev, { date: "", start: "20:00", end: "23:00" }])} className="w-full py-2 border border-dashed border-white/10 rounded-lg text-xs font-bold text-white/40 hover:text-white hover:border-white/30 transition-all">
                              + Add Session (Multi-Day)
                            </button>
                          </div>
                        )}

                        {scheduleMode === "series" && (
                          <div className="space-y-4 animate-in fade-in">
                            <div className="flex justify-between gap-1">
                              {SERIES_DAYS.map((day) => {
                                const isActive = !!recurring[day];
                                return (
                                  <button key={day} onClick={() => toggleSeriesDay(day)} className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase transition-all border ${isActive ? "bg-indigo-500/20 border-indigo-500 text-indigo-200" : "bg-black/20 border-white/5 text-white/30 hover:bg-white/5"}`}>{day}</button>
                                );
                              })}
                            </div>
                            <div className="space-y-2 border border-white/5 rounded-xl bg-black/20 p-2">
                              {Object.keys(recurring).length === 0 && (
                                <div className="text-center text-xs text-white/30 py-4 italic">Select days above to configure times.</div>
                              )}
                              {SERIES_DAYS.map((day) => {
                                const pattern = recurring[day];
                                if (!pattern) return null;
                                return (
                                  <div key={day} className="grid grid-cols-[2rem_1fr_auto_1fr] items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-2 animate-in slide-in-from-left-2">
                                    <div className="text-center text-xs font-bold text-indigo-300">{day}</div>
                                    <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1 min-w-0">
                                      <button onClick={() => setRecurring((p) => ({ ...p, [day]: { ...p[day], start: bumpHalfHour(p[day].start, -30) } }))} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">-</button>
                                      <input value={pattern.start} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-white focus:outline-none min-w-0" />
                                      <button onClick={() => setRecurring((p) => ({ ...p, [day]: { ...p[day], start: bumpHalfHour(p[day].start, 30) } }))} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">+</button>
                                    </div>
                                    <span className="text-white/20 text-xs px-1">to</span>
                                    <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1 min-w-0">
                                      <button onClick={() => setRecurring((p) => ({ ...p, [day]: { ...p[day], end: bumpHalfHour(p[day].end, -30) } }))} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">-</button>
                                      <input value={pattern.end} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-white focus:outline-none min-w-0" />
                                      <button onClick={() => setRecurring((p) => ({ ...p, [day]: { ...p[day], end: bumpHalfHour(p[day].end, 30) } }))} className="h-6 w-6 rounded bg-white/5 text-xs text-white/50 hover:text-white flex-shrink-0 flex items-center justify-center">+</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4" data-tt="suggested-times">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <label className="text-xs font-bold uppercase tracking-widest text-white/40 pl-1">Suggested Raid Times</label>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setSuggestWeek((o) => Math.max(0, o - 1))} disabled={suggestWeek === 0} className="p-0.5 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors disabled:opacity-20 disabled:cursor-not-allowed">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                          </button>
                          <span className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] font-bold text-white/70 min-w-[100px] text-center">{suggestWeekLabel}</span>
                          <button onClick={() => setSuggestWeek((o) => Math.min(1, o + 1))} disabled={suggestWeek >= 1} className="p-0.5 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors disabled:opacity-20 disabled:cursor-not-allowed">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                        <span className="text-xs font-bold text-emerald-400">{raidSize}</span>
                        <span className="text-[10px] text-emerald-400/60 uppercase">Players</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-3 px-1 mb-1">
                        <span className="text-[10px] text-white/30 font-medium">3 unconfirmed</span>
                      </div>
                      {suggestedTimes.map((s, i) => {
                        const sel = i === pickedTime;
                        const barCls = s.pct >= 80 ? "bg-emerald-500" : s.pct >= 50 ? "bg-amber-500" : "bg-red-500";
                        const pctCls = s.pct >= 80 ? "text-emerald-400" : s.pct >= 50 ? "text-amber-400" : "text-red-400";
                        return (
                          <button
                            key={i}
                            onClick={() => { setPickedTime(i); setSessions([{ date: dateForSuggestion(s.day), start: s.time, end: bumpHalfHour(s.time, 180) }]); setScheduleMode("single"); }}
                            className={`w-full p-3 rounded-xl border transition-all text-left flex items-center gap-4 ${sel ? "border-cyan-500 bg-cyan-500/10 ring-1 ring-cyan-500/50" : "border-white/10 bg-black/20 hover:bg-white/5 hover:border-white/20"}`}
                          >
                            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white/5 flex flex-col items-center justify-center">
                              <div className="text-[10px] font-bold text-white/40 uppercase">{s.day}</div>
                              <div className="text-sm font-black text-white">{s.time}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-white">{s.available}</span>
                                <span className="text-xs text-white/40">of {s.total} available</span>
                              </div>
                              <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${s.pct}%` }} />
                              </div>
                            </div>
                            <div className={`text-lg font-black ${pctCls}`}>{s.pct}%</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                </div>
              )}
              {raidTab === 1 && (
                <div className="grid h-full grid-cols-[450px_1fr] divide-x divide-white/10">
                  {/* LEFT: ROSTER POOL */}
                  <div data-tt="roster-pool" className="flex flex-col bg-black/20 h-full overflow-hidden">
                    <div className="flex items-center justify-between border-b border-white/10 p-5 bg-white/[0.02]">
                      <div className="text-xs font-bold uppercase tracking-widest text-white/60">Roster Pool</div>
                      <div className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-white/90">{availablePool.length} Players</div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 space-y-4 pr-2" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.1) transparent" }}>
                      {/* Role filter tabs */}
                      <div className="flex gap-1 flex-wrap mb-2">
                        <button onClick={() => setPoolFilter(null)} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all border ${!poolFilter ? "bg-white/10 border-white/20 text-white" : "border-white/5 text-white/30 hover:text-white/50 hover:bg-white/5"}`}>All ({poolCount(null)})</button>
                        {ROLE_TABS.map((r) => (
                          <button key={r} onClick={() => setPoolFilter(poolFilter === r ? null : r)} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all border flex items-center gap-1 ${poolFilter === r ? "bg-white/10 border-white/20 text-white" : "border-white/5 text-white/30 hover:text-white/50 hover:bg-white/5"}`}>
                            <span className="text-xs">{getRoleIcon(r)}</span>
                            <span>{r === "Melee DPS" ? "Melee" : r === "Ranged DPS" ? "Ranged" : r}</span>
                            <span className="opacity-50">({poolCount(r)})</span>
                          </button>
                        ))}
                      </div>

                      {/* Player cards */}
                      <div className="space-y-2">
                        {visiblePool.map((p) => {
                          const specs = poolFilter ? p.specs.filter((s) => s.role === poolFilter) : p.specs;
                          return (
                            <div key={p.name} className="rounded-lg border p-3 transition-all border-white/10 bg-black/40 hover:bg-white/[0.06]">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-bold text-white truncate">{p.name}{p.more ? <span className="text-white/40 font-normal"> & {p.more} more</span> : null}</span>
                                {p.squad != null && (
                                  <span className="flex items-center gap-0.5 text-[9px] text-indigo-300 bg-indigo-500/20 px-1 py-0.5 rounded">
                                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                    <span className="font-bold">{p.squad}</span>
                                  </span>
                                )}
                                <span className="text-[10px] text-white/30 ml-auto">{p.specs.length} spec{p.specs.length !== 1 ? "s" : ""}</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {specs.map((s, i) => {
                                  const roleColor = ROLE_COLORS[s.role] || "#fb923c";
                                  const entry = { player: p.name, name: s.char, cls: s.cls, role: s.role, spec: s.spec, score: s.score };
                                  return (
                                    <button
                                      key={i}
                                      draggable
                                      onDragStart={() => setDragSpec(entry)}
                                      onDragEnd={() => setDragSpec(null)}
                                      onClick={() => seatFirstEmpty(entry)}
                                      className="group flex items-center gap-1.5 h-8 w-[calc(50%-4px)] px-2.5 py-1.5 rounded-md border transition-all cursor-grab active:cursor-grabbing select-none hover:border-white/30 hover:bg-white/10 hover:shadow-lg"
                                      style={{ borderColor: `${roleColor}40`, backgroundColor: `${roleColor}10` }}
                                    >
                                      <span className="text-xs flex-shrink-0">{getRoleIcon(s.role)}</span>
                                      <span className="text-[10px] font-bold truncate" style={{ color: roleColor }}>{s.spec}</span>
                                      <span className="text-[9px] text-white/30 truncate max-w-[60px] flex-shrink-0" title={`${s.char} (${s.cls})`}>{s.char}</span>
                                      <span className="font-mono font-bold text-[10px] leading-none flex-shrink-0 ml-auto" style={{ color: getParseColor(s.score) }} title={`Warcraft Logs parse: ${s.score}`}>{s.score}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {visiblePool.length === 0 && poolFilter && (
                        <div className="text-center text-xs text-white/30 py-4">
                          No players with <span className="font-bold text-white/50">{poolFilter}</span> spec available.
                        </div>
                      )}

                      {/* Already Seated */}
                      {seatedPool.length > 0 && (
                        <div className="pt-6 border-t border-white/10">
                          <div className="mb-3 px-1 text-[10px] font-bold uppercase tracking-widest text-amber-500/60">Already Seated</div>
                          <div className="space-y-2 opacity-60 grayscale hover:grayscale-0 transition-all">
                            {seatedPool.map((p) => (
                              <div key={p.name} className="rounded-lg border border-white/10 bg-black/40 p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-bold text-white truncate">{p.name}{p.more ? <span className="text-white/40 font-normal"> & {p.more} more</span> : null}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {p.specs.map((s, i) => (
                                    <span key={i} className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/10 bg-white/5 text-[10px] text-white/50">
                                      <span className="text-xs">{getRoleIcon(s.role)}</span>
                                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CLASS_COLORS[s.cls] || "#888" }} />
                                      <span>{s.spec}</span>
                                      <span className="text-white/20">{s.char}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RIGHT: BOARD */}
                  <div className="flex flex-col bg-transparent min-h-0">
                    <div className="flex items-center justify-between border-b border-white/10 p-5 bg-white/[0.02]">
                      <div className="flex items-center gap-4">
                        <div className="text-xs font-bold uppercase tracking-widest text-white/60">Raid Roster</div>
                        {filledSeats > 0 && (
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 text-xs">
                              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>🛡️</span><span className="font-bold text-white">{comp["Tank"]}</span></span>
                              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>💚</span><span className="font-bold text-white">{comp["Healer"]}</span></span>
                              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>⚔️</span><span className="font-bold text-white">{comp["Melee DPS"]}</span></span>
                              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>🏹</span><span className="font-bold text-white">{comp["Ranged DPS"]}</span></span>
                            </div>
                            {seatedClassCounts.length > 0 && <span className="text-white/10">|</span>}
                            <div className="flex flex-wrap items-center gap-1">
                              {seatedClassCounts.map(([className, count]) => (
                                <span key={className} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/40 border border-white/5 text-[10px]" title={className}>
                                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CLASS_COLORS[className] || "#888" }} />
                                  <span className="font-bold" style={{ color: CLASS_COLORS[className] || "#fff" }}>{count}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-white/90">{filledSeats} / {raidSize} Filled</div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-8">
                      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {[0, 1].map((g) => (
                          <div key={g} className="rounded-xl border border-white/5 bg-transparent p-2 shadow-lg">
                            <div className="mb-2 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white/30 border-b border-white/5">Group {g + 1}</div>
                            <div className="space-y-1.5">
                              {Array.from({ length: 5 }).map((_, sIdx) => {
                                const idx = g * 5 + sIdx; const m = seats[idx];
                                return (
                                  <div key={idx} onDragOver={(e) => e.preventDefault()} onDrop={() => assignSeat(idx)}>
                                    <TrialRosterCard m={m} dragActive={!!dragSpec} onRemove={() => clearSeat(idx)} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* TRUE BENCH */}
                      <div onDragOver={(e) => e.preventDefault()} onDrop={addToBench} className="mt-6 rounded-xl border border-dashed border-amber-400/30 bg-amber-400/[0.03] p-4">
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <span className="text-xs font-bold uppercase tracking-widest text-amber-300/80">Bench (Backup)</span>
                          <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-200/90">{bench.length} {bench.length === 1 ? "Player" : "Players"}</span>
                          <span className="text-[10px] text-white/30">Counted like raiders for lockout &amp; bilocation, but outside the {raidSize} seats. Drag onto a seat to promote.</span>
                        </div>
                        {bench.length === 0 ? (
                          <div className="py-6 text-center text-[11px] text-white/25">Drag players here to add backups.</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                            {bench.map((m, i) => <TrialRosterCard key={i} m={m} onRemove={() => setBench((b) => b.filter((_, j) => j !== i))} />)}
                          </div>
                        )}
                      </div>

                      {/* Composition Coverage */}
                      {filledSeats > 0 && (
                        <div className="mt-6 rounded-xl border border-white/10 bg-black/30 backdrop-blur-sm">
                          <button onClick={() => setBuffOpen(!buffOpen)} className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-white/5 transition-colors rounded-t-xl">
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold uppercase tracking-widest text-white/60">Composition Coverage</span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${coveragePct >= 80 ? "bg-emerald-500/20 text-emerald-300" : coveragePct >= 50 ? "bg-amber-500/20 text-amber-300" : "bg-red-500/20 text-red-300"}`}>
                                {coverageCovered}/{coverageTotal} ({coveragePct}%)
                              </span>
                              {avgParse !== null && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/50 font-mono flex items-center gap-1" style={{ color: getParseColor(avgParse) }} title={`Average Warcraft Logs parse across ${seatedScores.length} seated character${seatedScores.length === 1 ? "" : "s"}`}>
                                  <span className="text-white/40 font-sans">AVG PARSE</span>
                                  {avgParse}
                                </span>
                              )}
                            </div>
                            <svg className={`w-4 h-4 text-white/40 transition-transform ${buffOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {buffOpen && (
                            <div className="px-5 pb-5 space-y-3 border-t border-white/5 columns-2 gap-6">
                              {coverage.map((group) => (
                                <div key={group.category} className="pt-3 break-inside-avoid">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">{group.category}</span>
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${group.coveredCount === group.tags.length ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/30"}`}>
                                      {group.coveredCount}/{group.tags.length}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {group.tags.map((tc: any) => (
                                      <span
                                        key={tc.label}
                                        title={tc.covered ? `${tc.label}: ${tc.providers.join(", ")}` : `${tc.label}: MISSING`}
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border transition-all ${tc.covered ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-white/[0.02] border-white/5 text-white/20"}`}
                                      >
                                        {tc.label}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {raidTab === 2 && (
                <div className="h-full overflow-y-auto p-8">
                <div className="mx-auto max-w-3xl space-y-8">
                  {/* RAID SUMMARY */}
                  <div className="rounded-2xl border border-white/10 bg-black/40 p-8 backdrop-blur-sm shadow-xl">
                    <h3 className="mb-6 text-xs font-bold uppercase tracking-widest text-white/40 border-b border-white/5 pb-2">Raid Summary</h3>
                    <div className="grid gap-6 md:grid-cols-3">
                      <div>
                        <div className="text-[10px] uppercase text-white/40 font-bold mb-1">Instance</div>
                        <div className="text-xl font-bold text-white">{raidLabel || "Untitled Raid"}</div>
                        <div className="text-sm text-cyan-400">{raidOptions.find((o) => o.id === raidOptId)?.label}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-white/40 font-bold mb-1">Schedule</div>
                        <div className="text-xl font-bold text-white">
                          {scheduleMode === "series" ? (
                            <span className="text-indigo-300">Series: {Object.keys(recurring).join(", ")}</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {(scheduleMode === "single" ? [s0] : sessions).map((s, i) => (
                                <span key={i} className="text-sm text-emerald-200">
                                  {s.date ? new Date(s.date).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "Pick a date"} @ {s.start}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {scheduleMode === "series" && <div className="text-sm text-white/50">Recurring Weekly</div>}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-white/40 font-bold mb-1">Roster</div>
                        <div className="text-xl font-bold text-emerald-400">{filledSeats} / {raidSize}</div>
                        <div className="text-sm text-white/50">Players Assigned</div>
                      </div>
                    </div>
                  </div>

                  {/* LEADERSHIP */}
                  <div className="rounded-2xl border border-white/10 bg-black/40 p-6 backdrop-blur-sm shadow-xl">
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-white/40 border-b border-white/5 pb-2">Leadership</h3>
                    <div className="grid grid-cols-2 gap-6">
                      {[["Raid Leader", raidLeader, setRaidLeader], ["Master Looter", lootMaster, setLootMaster]].map(([label, val, setter]: any) => (
                        <div key={label} className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-white/40">{label}</label>
                          <div className="relative">
                            <select value={val} onChange={(e) => setter(e.target.value)} className="w-full appearance-none rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white focus:border-cyan-500 focus:outline-none transition-all hover:bg-black/60">
                              <option value="">Unassigned</option>
                              {seats.filter(Boolean).map((s: any, i: number) => <option key={i} value={s.name}>{s.name}</option>)}
                            </select>
                            <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40 text-xs">▼</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* LOOT RULES */}
                  <div className="rounded-2xl border border-white/10 bg-black/40 p-6 backdrop-blur-sm shadow-xl">
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-white/40 border-b border-white/5 pb-2">Loot Rules</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-bold text-white">Enable Soft Reserves</div>
                          <div className="text-xs text-white/40 mt-0.5">Players can reserve items they want before the raid</div>
                        </div>
                        <button onClick={() => setSoftReserves((v) => !v)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${softReserves ? "bg-cyan-600" : "bg-white/10"}`}>
                          <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${softReserves ? "translate-x-6" : "translate-x-1"}`} />
                        </button>
                      </div>
                      {softReserves && (
                        <div className="flex items-center justify-between pl-4 border-l-2 border-cyan-500/30">
                          <div>
                            <div className="text-sm font-medium text-white">Reserves Per Player</div>
                            <div className="text-xs text-white/40 mt-0.5">How many items each player can soft reserve</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setSrMax(Math.max(1, srMax - 1))} className="h-8 w-8 rounded-lg border border-white/10 bg-black/40 text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center transition-all text-lg font-bold">−</button>
                            <span className="w-8 text-center text-lg font-bold text-cyan-400">{srMax}</span>
                            <button onClick={() => setSrMax(Math.min(10, srMax + 1))} className="h-8 w-8 rounded-lg border border-white/10 bg-black/40 text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center transition-all text-lg font-bold">+</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RAID COMPOSITION */}
                  <div className="rounded-2xl border border-white/10 bg-black/40 p-6 backdrop-blur-sm shadow-xl">
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-white/40 border-b border-white/5 pb-2">Raid Composition</h3>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {ROLE_TABS.map((role) => {
                        const members = seats.filter((s: any) => s?.role === role);
                        const label = role === "Melee DPS" ? "Melee" : role === "Ranged DPS" ? "Ranged" : role + "s";
                        return (
                          <div key={role} className="p-3 rounded-lg bg-white/5 border border-white/10">
                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/5">
                              <span className="text-lg">{getRoleIcon(role)}</span>
                              <span className="text-sm font-bold text-white">{comp[role]}</span>
                              <span className="text-[10px] uppercase text-white/40 font-bold">{label}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {members.length > 0 ? members.map((c: any, i: number) => (
                                <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: `${CLASS_COLORS[c.cls] || "#888"}20`, color: CLASS_COLORS[c.cls] || "#fff" }}>{c.name}</span>
                              )) : (
                                <span className="text-[10px] text-white/30 italic">None</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[10px] uppercase text-white/40 font-bold mb-2">Class Breakdown</div>
                    <div className="flex flex-wrap gap-2">
                      {seatedClassCounts.map(([className, count]) => (
                        <div key={className} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/40 border border-white/10">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: CLASS_COLORS[className] || "#888" }} />
                          <span className="text-xs font-bold" style={{ color: CLASS_COLORS[className] || "#fff" }}>{count}</span>
                          <span className="text-xs text-white/50">{className}</span>
                        </div>
                      ))}
                      {seatedClassCounts.length === 0 && (
                        <div className="text-xs text-white/30 italic">No players assigned yet</div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-xs font-bold uppercase tracking-widest text-white/40 pl-1">Raid Notes</label>
                    <textarea value={raidNotes} onChange={(e) => setRaidNotes(e.target.value)} placeholder="Consumable requirements, loot rules, Discord links, boss assignments..." className="h-40 w-full rounded-xl border border-white/10 bg-black/40 p-4 text-sm text-white placeholder:text-white/20 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all" />
                  </div>
                </div>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="relative z-10 flex h-20 shrink-0 items-center justify-between border-t border-white/10 bg-black/60 px-8">
              <button onClick={() => setStep(STEPS.indexOf("plannerNav"))} className="rounded-lg px-4 py-2 text-sm font-medium text-white/50 hover:text-white hover:bg-white/5 transition-colors">Cancel</button>
              <div className="flex gap-4">
                {raidTab > 0 && <button onClick={goRaidBack} className="rounded-lg border border-white/10 bg-white/5 px-6 py-2.5 text-sm font-bold text-white hover:bg-white/10 hover:border-white/20 transition-all">Back</button>}
                {raidTab < 2 ? (
                  <button onClick={goRaidAdvance} className="rounded-lg bg-cyan-600 px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 hover:bg-cyan-500 hover:shadow-cyan-500/40 transition-all">Next Step</button>
                ) : (
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-white/10 bg-white/[0.03]">
                      <button
                        type="button"
                        onClick={() => setReleaseOnCreate((prev) => !prev)}
                        className={`relative w-10 h-5 rounded-full transition-colors duration-200 shrink-0 ${releaseOnCreate ? "bg-amber-500" : "bg-white/10"}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${releaseOnCreate ? "translate-x-5" : ""}`} />
                      </button>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-white/70">Release bench to PUG — {suggestWeekLabel}</span>
                        <span className="text-[10px] text-white/30">Opted-in characters not assigned will be available to other raids this lockout</span>
                      </div>
                    </div>
                    <button onClick={() => { setPublished(false); goRaidAdvance(); }} className="rounded-lg bg-white/[0.06] border border-white/10 px-6 py-2.5 text-sm font-bold text-white/70 hover:bg-white/10 hover:text-white transition-all shrink-0">Save</button>
                    <button data-tt="publish-raid" onClick={() => { setPublished(true); goRaidAdvance(); }} className="rounded-lg bg-emerald-600 px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/25 hover:bg-emerald-500 hover:shadow-emerald-500/40 transition-all shrink-0">{scheduleMode === "series" ? "Create Series" : "Publish"}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        ) : (
        /* PLANNER stage (identical to PlannerPage's raid detail view) */
        <section ref={plannerSectionRef} className="scroll-mt-24">
          <div className="max-w-5xl mx-auto pb-10 relative">
            {/* HEADER CARD */}
            <div className="relative mb-6 overflow-hidden rounded-xl border border-white/10 bg-[#0F1115] shadow-2xl">
              <div className="relative z-10 p-6">
                <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch justify-between">
                  {/* Left: Info */}
                  <div className="space-y-4 flex-1">
                    {/* Guild Logo + Meta Badges */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 shrink-0 rounded-full bg-indigo-500/20 border-2 border-indigo-500/30 flex items-center justify-center text-sm text-indigo-300 font-bold shadow-lg">
                        {(gName || "G").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-bold text-white/60">{gName || "Your Guild"}</span>
                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-white/40">
                          {!published && (
                            <span className="px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/15 text-amber-300">Draft</span>
                          )}
                          <span className="text-cyan-400">{games.find((g) => g.id === gameId)?.name || "World of Warcraft"}</span>
                          <span>•</span>
                          <span>{gRealm || "Unknown Realm"}</span>
                          <span className="px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-white/70">{raidSize}-Man</span>
                        </div>
                      </div>
                    </div>

                    {/* Title */}
                    <div>
                      <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-none">{raidName}</h1>
                    </div>

                    {/* Session Time */}
                    <div className="flex flex-wrap items-center gap-2">
                      {sessionChips.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-200 text-xs font-bold">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          {s}
                        </div>
                      ))}
                      {sessionChips.length > 0 && (
                        <span className="text-[10px] text-white/25 font-medium">{tzShort}</span>
                      )}
                    </div>

                    {/* Leaders + SR Status */}
                    <div className="flex flex-wrap items-center gap-4 text-xs pt-1">
                      {raidLeader && (
                        <div className="flex items-center gap-2">
                          <span className="text-purple-400 font-bold uppercase text-[10px] tracking-wider">Raid Leader</span>
                          <span className="text-white font-medium bg-black/40 px-2 py-1 rounded border border-white/10">{raidLeader}</span>
                        </div>
                      )}
                      {lootMaster && (
                        <div className="flex items-center gap-2">
                          <span className="text-amber-400 font-bold uppercase text-[10px] tracking-wider">Loot Master</span>
                          <span className="text-white font-medium bg-black/40 px-2 py-1 rounded border border-white/10">{lootMaster}</span>
                        </div>
                      )}
                      {softReserves && (
                        <div className="flex items-center gap-2">
                          <span className="text-cyan-400 font-bold uppercase text-[10px] tracking-wider">Soft Reserves</span>
                          <span className="font-medium px-2 py-1 rounded border text-xs bg-cyan-500/10 border-cyan-500/20 text-cyan-200">0/{srMax}</span>
                        </div>
                      )}
                    </div>

                    {/* Raid Notes */}
                    {raidNotes.trim() && (
                      <div className="mt-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <span className="text-white/30 font-bold uppercase text-[10px] tracking-wider">Notes</span>
                        <p className="text-white/60 text-xs mt-1 whitespace-pre-wrap leading-relaxed">{raidNotes}</p>
                      </div>
                    )}
                  </div>

                  {/* Right: Save / Publish / Close */}
                  <div className="flex flex-col gap-3 shrink-0 self-start">
                    <div className="flex items-center gap-0 justify-end">
                      <button disabled className="h-10 px-6 rounded-l-lg font-bold text-xs uppercase tracking-wide transition-all flex items-center justify-center gap-2 bg-white/[0.06] text-white/30 cursor-not-allowed border border-white/10">
                        Save
                      </button>
                      <button
                        onClick={() => setPublished((v) => !v)}
                        className={`h-10 px-4 font-bold text-xs uppercase tracking-wide transition-all flex items-center justify-center gap-2 border-l-0 ${
                          published
                            ? "bg-amber-500/10 border border-amber-500/25 border-l-0 text-amber-300/70 hover:text-amber-200 hover:border-amber-500/40 hover:bg-amber-500/20"
                            : "bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-500/20"
                        }`}
                      >
                        {published ? (
                          <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg> Unpublish</>
                        ) : (
                          <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> Publish</>
                        )}
                      </button>
                      <button
                        onClick={() => setStep(STEPS.indexOf("plannerNav"))}
                        className="h-10 w-10 bg-white/[0.08] hover:bg-red-500/15 border border-white/15 rounded-r-lg border-l-0 text-white/50 hover:text-red-300 transition-colors flex items-center justify-center"
                        title="Close"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Raid Controls */}
                <div className="mt-5 pt-5 border-t border-white/10">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => { setRaidTab(0); setStep(STEPS.indexOf("raid")); }} className="h-10 px-4 bg-indigo-500/10 border border-indigo-500/25 text-indigo-300/80 hover:text-indigo-200 hover:border-indigo-400/40 hover:bg-indigo-500/20 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      Edit Raid
                    </button>
                    <button onClick={() => showNotice("RAIDTOOLS")} className="h-10 px-4 bg-yellow-500/10 border border-yellow-500/20 text-yellow-300/80 hover:text-yellow-200 hover:border-yellow-400/40 hover:bg-yellow-500/20 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                      Rate Players
                    </button>
                    <button onClick={() => showNotice("RAIDTOOLS")} className="h-10 px-4 bg-purple-500/10 border border-purple-500/20 text-purple-300/80 hover:text-purple-200 hover:border-purple-400/40 hover:bg-purple-500/20 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      Soft Reserve
                    </button>
                    <button onClick={() => showNotice("RAIDTOOLS")} className="h-10 px-4 bg-teal-500/10 border border-teal-500/20 text-teal-300/80 hover:text-teal-200 hover:border-teal-400/40 hover:bg-teal-500/20 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h6" /></svg>
                      Assignments
                    </button>
                    <button onClick={() => showNotice("RAIDTOOLS")} className="h-10 px-4 bg-violet-500/10 border border-violet-500/20 text-violet-300/80 hover:text-violet-200 hover:border-violet-400/40 hover:bg-violet-500/20 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a3 3 0 100 6m0-6a3 3 0 110 6m0 0v2m0-2a3 3 0 100 6m0-6a3 3 0 110 6v2m-8-9h2m12 0h-2M7.05 7.05l1.414 1.414m7.072 7.072 1.414 1.414m0-9.9-1.414 1.414m-7.072 7.072-1.414 1.414" />
                      </svg>
                      Loot Council
                    </button>
                    <button
                      onClick={() => { setPugCopied(true); setTimeout(() => setPugCopied(false), 2500); }}
                      className={`h-10 px-4 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                        pugCopied
                          ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
                          : "bg-orange-500/10 border border-orange-500/20 text-orange-300/80 hover:text-orange-200 hover:border-orange-400/40 hover:bg-orange-500/20"
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                      {pugCopied ? "Link Copied!" : "PUG Invite Link"}
                    </button>
                    <div className="flex-1" />
                    <button onClick={() => showNotice("RAIDTOOLS")} className="h-10 px-4 text-white/40 hover:text-red-400 hover:bg-red-500/15 border border-white/10 hover:border-red-500/30 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2" title="Delete Raid">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* MAIN GRID */}
            <div className="grid gap-8 lg:grid-cols-[1fr_340px] items-start">
              <div className="space-y-4">
                {/* Compact Composition Summary */}
                <div className="flex flex-wrap items-center gap-3 px-1 py-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>🛡️</span><span className="font-bold text-white">{comp["Tank"]}</span></span>
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>💚</span><span className="font-bold text-white">{comp["Healer"]}</span></span>
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>⚔️</span><span className="font-bold text-white">{comp["Melee DPS"]}</span></span>
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10"><span>🏹</span><span className="font-bold text-white">{comp["Ranged DPS"]}</span></span>
                  </div>
                  {seatedClassCounts.length > 0 && <span className="text-white/10">|</span>}
                  <div className="flex flex-wrap items-center gap-1">
                    {seatedClassCounts.map(([className, count]) => (
                      <span key={className} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/40 border border-white/5 text-[10px]" title={className}>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CLASS_COLORS[className] || "#888" }} />
                        <span className="font-bold" style={{ color: CLASS_COLORS[className] || "#fff" }}>{count}</span>
                      </span>
                    ))}
                  </div>
                  <span className="text-[10px] text-white/30 ml-auto">{filledSeats}/{raidSize}</span>
                </div>

                {/* GROUP GRID */}
                <div className="grid grid-cols-2 gap-4">
                  {[0, 1].map((groupIdx) => (
                    <div key={groupIdx} className="bg-black/40 border border-white/10 rounded-xl p-2">
                      <div className="space-y-1">
                        {Array.from({ length: 5 }).map((_, slotIdx) => {
                          const idx = groupIdx * 5 + slotIdx;
                          const m = seats[idx];
                          return (
                            <div key={idx} onDragOver={(e) => e.preventDefault()} onDrop={() => assignSeat(idx)}>
                              {m ? (
                                <TrialDraggableCard m={m} onRemove={() => clearSeat(idx)} />
                              ) : (
                                <div className="h-[48px] border border-dashed border-white/10 rounded flex items-center justify-center text-xs text-white/20">{dragSpec ? "Drop here" : "Empty"}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* TRUE BENCH */}
                <div onDragOver={(e) => e.preventDefault()} onDrop={addToBench} className="mt-4 rounded-xl border border-dashed border-amber-400/30 bg-amber-400/[0.03] p-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300/80">Bench</span>
                    <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-200/90">{bench.length}</span>
                    <span className="text-[10px] text-white/30">Drag a player here to bench them; drag onto a seat to promote.</span>
                  </div>
                  {bench.length === 0 ? (
                    <div className="py-4 text-center text-[11px] text-white/25">Drag players here to add backups.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {bench.map((m, i) => <TrialDraggableCard key={i} m={m} onRemove={() => setBench((b) => b.filter((_, j) => j !== i))} />)}
                    </div>
                  )}
                </div>

                {/* Buff Coverage Panel */}
                {filledSeats > 0 && (
                  <div className="mt-4 rounded-xl border border-white/10 bg-black/30 backdrop-blur-sm">
                    <button onClick={() => setBuffOpen(!buffOpen)} className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-white/5 transition-colors rounded-t-xl">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold uppercase tracking-widest text-white/60">Composition Coverage</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${coveragePct >= 80 ? "bg-emerald-500/20 text-emerald-300" : coveragePct >= 50 ? "bg-amber-500/20 text-amber-300" : "bg-red-500/20 text-red-300"}`}>
                          {coverageCovered}/{coverageTotal} ({coveragePct}%)
                        </span>
                        {avgParse !== null && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/50 font-mono flex items-center gap-1" style={{ color: getParseColor(avgParse) }}>
                            <span className="text-white/40 font-sans">AVG PARSE</span>
                            {avgParse}
                          </span>
                        )}
                      </div>
                      <svg className={`w-4 h-4 text-white/40 transition-transform ${buffOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {buffOpen && (
                      <div className="px-5 pb-5 space-y-3 border-t border-white/5 columns-2 gap-6">
                        {coverage.map((group) => (
                          <div key={group.category} className="pt-3 break-inside-avoid">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">{group.category}</span>
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${group.coveredCount === group.tags.length ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/30"}`}>
                                {group.coveredCount}/{group.tags.length}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {group.tags.map((tc: any) => (
                                <span key={tc.label} title={tc.covered ? `${tc.label}: ${tc.providers.join(", ")}` : `${tc.label}: MISSING`} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border transition-all ${tc.covered ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-white/[0.02] border-white/5 text-white/20"}`}>
                                  {tc.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT SIDEBAR */}
              <div className="space-y-6">
                {/* LEADERSHIP CONTROLS */}
                <div className="bg-[#0e121b] border border-white/10 rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-bold uppercase text-white/60">Leadership</h3>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-purple-400">Raid Leader</label>
                    <select value={raidLeader} onChange={(e) => setRaidLeader(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-purple-500/50 outline-none">
                      <option value="">-- Select Leader --</option>
                      {seats.filter(Boolean).map((s: any, i: number) => <option key={i} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-amber-400">Loot Master</label>
                    <select value={lootMaster} onChange={(e) => setLootMaster(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:border-amber-500/50 outline-none">
                      <option value="">-- Select Master --</option>
                      {seats.filter(Boolean).map((s: any, i: number) => <option key={i} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* ROSTER POOL */}
                <div data-tt="detail-pool" className="bg-[#0e121b] border border-white/10 rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <h3 className="text-xs font-bold uppercase text-white/60">Roster Pool</h3>
                    <button onClick={() => setSuggestionsOpen(true)} className="text-cyan-400 text-xs border border-cyan-500/30 px-2 py-1 rounded bg-cyan-500/10 hover:bg-cyan-500/20">
                      Check Suggestions
                    </button>
                  </div>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto">
                    {/* Available Players */}
                    {availablePool.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400/70 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                          Available ({availablePool.length})
                        </div>
                        {availablePool.map((p) => {
                          const s0p = p.specs[0];
                          const entry = { player: p.name, name: s0p.char, cls: s0p.cls, role: s0p.role, spec: s0p.spec, score: s0p.score };
                          return (
                            <TrialDraggableCard
                              key={p.name}
                              m={{ name: p.name, cls: s0p.cls, score: s0p.score, squad: p.squad, specsRow: p.specs }}
                              draggable
                              onDragStart={() => setDragSpec(entry)}
                              onDragEnd={() => setDragSpec(null)}
                            />
                          );
                        })}
                      </div>
                    )}

                    {/* Unavailable Buckets */}
                    {Object.entries(
                      MOCK_UNAVAILABLE.reduce((acc: Record<string, typeof MOCK_UNAVAILABLE>, c) => {
                        (acc[c.reason] = acc[c.reason] || []).push(c);
                        return acc;
                      }, {})
                    ).map(([reason, players]) => {
                      const cfg = BUCKET_CONFIG[reason] || BUCKET_CONFIG["Unavailable"];
                      return (
                        <div key={reason} className="space-y-2">
                          <div className={`text-[10px] font-bold uppercase tracking-wider ${cfg.color} flex items-center gap-2`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotColor}`}></span>
                            {cfg.label} ({players.length})
                          </div>
                          {players.map((c) => (
                            <TrialDraggableCard key={c.name} m={{ name: c.name, cls: c.cls, score: c.score, specsRow: c.roles.map((r) => ({ spec: r, role: r })) }} unavailableReason={cfg.label} />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}
          </div>
        </main>
      </div>

      {/* SPOTLIGHT: dims the page with a hole cut around the current step's
          target control (so only the thing to click glows); falls back to a
          plain dim on decision steps with no target. */}
      {dimActive && (spotRect ? (
        <div
          className="fixed z-[184] pointer-events-none rounded-xl ring-2 ring-cyan-400/80 transition-all duration-200"
          style={{
            top: spotRect.top - 6,
            left: spotRect.left - 6,
            width: spotRect.width + 12,
            height: spotRect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.45), 0 0 28px rgba(34,211,238,0.35)",
          }}
        >
          {spotBurst && (
            <>
              <span className="absolute -inset-2 rounded-2xl ring-4 ring-cyan-300/80 animate-pulse" />
              <span className="absolute inset-0 rounded-xl bg-cyan-400/10 animate-pulse" />
            </>
          )}
        </div>
      ) : (
        <div className="fixed inset-0 z-[150] bg-black/60 transition-opacity" />
      ))}

      {/* CHECK SUGGESTIONS MODAL (mock) */}
      {suggestionsOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSuggestionsOpen(false)} />
          <div className="relative w-full max-w-md bg-[#0a0a0f] border border-white/15 rounded-xl shadow-2xl flex flex-col max-h-[80vh]">
            <div className="p-5 border-b border-white/10 flex justify-between items-center">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-white">Suggested Players</h3>
                <p className="text-[11px] text-white/40 mt-0.5">Best fits for your open seats, by availability and role.</p>
              </div>
              <button onClick={() => setSuggestionsOpen(false)} className="text-white/40 hover:text-white">✕</button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto">
              {MOCK_POOL.filter((p) => !seatedPlayers.has(p.name)).slice(0, 6).map((p) => {
                const sp = p.specs[0]; const color = CLASS_COLORS[sp.cls] || "#fff";
                const full = !seats.some((s) => !s);
                return (
                  <div key={p.name} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.03]">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-black shrink-0" style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}55` }}>{p.name.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate" style={{ color }}>{p.name}</div>
                      <div className="text-[11px] text-white/40">{sp.spec} · {sp.role}</div>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded">Available</span>
                    <button disabled={full} onClick={() => setSeats((prev) => { const n = [...prev]; const idx = n.findIndex((s) => !s); if (idx >= 0) n[idx] = { player: p.name, name: sp.char, cls: sp.cls, role: sp.role, spec: sp.spec, score: sp.score }; return n; })} className={`text-xs font-bold px-3 py-1.5 rounded border ${full ? "text-white/30 border-white/10 cursor-not-allowed" : "text-cyan-300 border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20"}`}>Add</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* COACH: anchored beside the spotlight target when there is one,
          otherwise centered at the bottom. A slim progress bar keeps the
          player oriented on the road to a published raid. */}
      {(() => {
        const coach = (
          <div className="space-y-2">
            {progress && (
              <div className="flex items-center gap-2 px-1">
                <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-cyan-400/70 rounded-full transition-all duration-500" style={{ width: `${(progress.n / progress.total) * 100}%` }} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 shrink-0">{progress.n} / {progress.total} · {progress.label}</span>
              </div>
            )}
            <CoachCard
              step={cur}
              charDone={!!createdChar}
              availCount={availCount}
              tileSpec={tile?.specLabel || null}
              charName={createdChar?.name || charName}
              guildDone={guildCreated}
              guildName={gName}
              raidName={raidName}
              raidBuilt={raidBuilt}
              invitePhase={invitePhase}
              onInviteNext={() => setInvitePhase("nav")}
              onInviteBack={() => setInvitePhase("code")}
              onBack={() => setStep((s) => Math.max(0, s - 1))}
              onNext={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              onAdd={() => { setCharError(null); setModalOpen(true); if (games.length && !gameId) setGameId(games[0].id); }}
              onCreateGuild={() => { setGuildError(null); setGuildModalOpen(true); }}
              onGuildSkip={() => { setGuildCreated(false); persist((d) => { d.guild = null; d.branch = "soloChar"; }); setStep(STEPS.length - 1); }}
              raidTab={raidTab}
              onRaidAdvance={goRaidAdvance}
              onRaidBack={goRaidBack}
              onKeep={onKeep}
              onStartOver={onStartOver}
            />
          </div>
        );
        // Re-runs the entrance animation whenever the step (or sub-step)
        // changes, so the card itself announces "new instruction".
        const animKey = `${cur}:${raidTab}:${invitePhase}:${createdChar ? 1 : 0}:${guildCreated ? 1 : 0}`;
        if (spotRect && dimActive && spotRect.height <= window.innerHeight * 0.55) {
          // Compact target: anchor the coach directly to it. Tall targets
          // (whole columns) fall through to bottom-center so the coach never
          // sits on top of the area the player is about to work in.
          const W = Math.min(520, window.innerWidth - 32);
          const margin = 18;
          const clampLeft = (l: number) => Math.min(Math.max(16, l), window.innerWidth - W - 16);
          let style: React.CSSProperties | null = null;
          if (spotRect.top + spotRect.height + 260 < window.innerHeight) {
            style = { top: spotRect.top + spotRect.height + margin, left: clampLeft(spotRect.left + spotRect.width / 2 - W / 2), width: W };
          } else if (spotRect.top > 280) {
            style = { bottom: window.innerHeight - spotRect.top + margin, left: clampLeft(spotRect.left + spotRect.width / 2 - W / 2), width: W };
          }
          if (style) {
            return (
              <div className="fixed z-[190]" style={style}>
                <div key={animKey} className="animate-in fade-in slide-in-from-bottom-3 duration-300">{coach}</div>
              </div>
            );
          }
        }
        return (
          <div className="fixed inset-x-0 bottom-0 z-[190] flex justify-center px-4 pb-6 pointer-events-none">
            <div key={animKey} className="w-full max-w-2xl pointer-events-auto animate-in fade-in slide-in-from-bottom-3 duration-300">{coach}</div>
          </div>
        );
      })()}

      {/* CHARACTER CREATE MODAL (above the spotlight) */}
      {modalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => { setModalOpen(false); setCharError(null); }} />
          <div className="relative w-full max-w-3xl bg-[#09090b] border border-white/10 rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-5 border-b border-white/10 bg-white/[0.02] flex justify-between items-center">
              <h3 className="text-sm font-bold uppercase tracking-widest text-white">Add Character</h3>
              <button onClick={() => { setModalOpen(false); setCharError(null); }} className="text-white/40 hover:text-white">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {charError && <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">{charError}</div>}
              {!groups.length ? (
                <div className="text-white/40 text-sm animate-pulse">Loading character options…</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    {games.length > 1 && (
                      <div className="space-y-1.5">
                        <label className={labelCls}>Game</label>
                        <select value={gameId} onChange={(e) => { setGameId(e.target.value); setSelectedOptions({}); setCharError(null); }} className={selectCls}>
                          {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <label className={labelCls}>Character Name</label>
                      <input value={charName} onChange={(e) => { setCharName(e.target.value); setCharError(null); }} maxLength={24} className={inputCls} placeholder="Name" />
                    </div>
                    <FormGroups groups={groups.filter((g) => ["region", "realm", "faction"].includes(g.key))} ctx={{ groups, selectedOptions, setSelectedOptions, readyToRaid, setReadyToRaid }} />
                  </div>
                  <div className="space-y-5">
                    <FormGroups groups={groups.filter((g) => g.key === "class")} ctx={{ groups, selectedOptions, setSelectedOptions, readyToRaid, setReadyToRaid }} />
                    <FormGroups groups={groups.filter((g) => g.key === "spec")} ctx={{ groups, selectedOptions, setSelectedOptions, readyToRaid, setReadyToRaid }} />
                    <FormGroups groups={groups.filter((g) => g.key === "raid_interest" || g.key === "raid_preferences")} ctx={{ groups, selectedOptions, setSelectedOptions, readyToRaid, setReadyToRaid }} />
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-white/10 bg-white/[0.02]">
              <button onClick={submitCharacter} disabled={!groups.length} className="w-full py-2.5 text-white font-bold text-xs uppercase tracking-widest rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 transition-all">Add Character</button>
            </div>
          </div>
        </div>
      )}

      {/* GUILD CREATE MODAL ("Create Guild", faithful to GuildsPage) */}
      {guildModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setGuildModalOpen(false)} />
          <div className="relative w-full max-w-lg bg-[#09090b] border border-white/10 rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-5 border-b border-white/10 bg-white/[0.02] flex justify-between items-center">
              <h3 className="text-sm font-bold uppercase tracking-widest text-white">Create Guild</h3>
              <button onClick={() => setGuildModalOpen(false)} className="text-white/40 hover:text-white">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {games.length > 1 && (
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Game Universe</label>
                  <select value={gameId} onChange={(e) => setGameId(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none">
                    {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-1">
                  <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Guild Name</label>
                  <input value={gName} onChange={(e) => { setGName(e.target.value); setGuildError(null); }} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Tag</label>
                  <input value={gTag} onChange={(e) => { setGTag(e.target.value); setGuildError(null); }} maxLength={6} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <GuildField label="Region" value={gRegion} onChange={(v) => { setGRegion(v); setGRealm(""); }} options={guildRegionOpts.map((o) => o.label)} placeholder="Select Region..." />
                <GuildField label="Realm" value={gRealm} onChange={setGRealm} options={guildRealmOpts.map((o) => o.label)} placeholder={guildSelectedRegionKey ? "Select Realm..." : "Select Region first..."} disabled={!guildSelectedRegionKey} />
              </div>
              <GuildField label="Faction" value={gFaction} onChange={setGFaction} options={guildFactionOpts.map((o) => o.label)} placeholder="Select Faction..." />
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">About Us</label>
                <textarea value={gAbout} onChange={(e) => setGAbout(e.target.value)} rows={3} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none resize-none" placeholder="Tell players what your guild is about..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Schedule</label>
                  <input value={gSchedule} onChange={(e) => setGSchedule(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none" placeholder="e.g. Tue/Thu 20:00" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Contact</label>
                  <input value={gContact} onChange={(e) => setGContact(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none" placeholder="Discord link / User" />
                </div>
              </div>
              {guildError && <div className="p-3 bg-red-900/20 border border-red-500/20 rounded text-xs text-red-300 text-center">{guildError}</div>}
            </div>
            <div className="p-5 border-t border-white/10 bg-white/[0.02]">
              <button onClick={submitGuild} className="w-full py-2.5 bg-white text-black font-bold text-xs uppercase tracking-widest rounded hover:bg-gray-200 transition-all">Create Guild</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// === Coach card (drives the steps) =========================================
// UX contract per step: say WHAT the highlighted thing is, what to DO with it,
// and WHY it matters for running a raid. The spotlight (in the parent) rings
// the one control the copy refers to.
function ChecklistIcon({ done }: { done: boolean }) {
  return done ? (
    <svg className="w-3.5 h-3.5 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
  ) : (
    <span className="w-3.5 h-3.5 shrink-0 rounded-full border border-white/25 inline-block" />
  );
}
function CoachCard({
  step, charDone, availCount, tileSpec, charName, guildDone, guildName, raidName, raidBuilt, raidTab, invitePhase,
  onInviteNext, onInviteBack, onBack, onNext, onAdd, onCreateGuild, onGuildSkip, onRaidAdvance, onRaidBack, onKeep, onStartOver,
}: {
  step: Step; charDone: boolean; availCount: number; tileSpec: string | null; charName: string;
  guildDone: boolean; guildName: string; raidName: string; raidBuilt: boolean; raidTab: number; invitePhase: "code" | "nav";
  onInviteNext: () => void; onInviteBack: () => void;
  onBack: () => void; onNext: () => void; onAdd: () => void; onCreateGuild: () => void; onGuildSkip: () => void; onRaidAdvance: () => void; onRaidBack: () => void; onKeep: (includeGuild: boolean) => void; onStartOver: () => void;
}) {
  if (step === "intro") {
    return (
      <div className="space-y-3">
        <Coach>
          <span className="font-bold text-white">Welcome. In about two minutes you'll run a raid from scratch.</span>
          <span className="block mt-1 text-white/60">The route: create a character, mark your raid hours, create a guild, grab its invite code, then schedule and publish a raid. Nothing is saved unless you keep it at the end.</span>
        </Coach>
        <div className="flex justify-end"><PrimaryBtn onClick={onNext}>Let's go →</PrimaryBtn></div>
      </div>
    );
  }
  if (step === "characters") {
    return (
      <div className="space-y-3">
        <Coach pose="point">
          {charDone ? (
            <><span className="font-bold text-white">That's your character card.</span> Your guild sees your class, spec and Warcraft Logs here, it's how raid leaders judge who fits where. Next: when can you actually raid?</>
          ) : (
            <><span className="font-bold text-white">Everything starts with a character.</span> Hit the highlighted <span className="text-cyan-300 font-bold">+ New Character</span> and pick your class and spec, the same form real members use.</>
          )}
        </Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          {charDone ? <PrimaryBtn onClick={onNext}>Next: raid hours →</PrimaryBtn> : <PrimaryBtn onClick={onAdd}>+ New Character</PrimaryBtn>}
        </div>
      </div>
    );
  }
  if (step === "availability") {
    return (
      <div className="space-y-3">
        <Coach pose="explain">
          <span className="font-bold text-white">Paint the hours you can raid.</span> Click-drag across the grid. This is the engine behind everything: when you schedule a raid later, Rallify ranks the times when the most people are free. Watch for it.{" "}
          {availCount > 0 && <span className="text-emerald-300 font-bold">{availCount} slot{availCount === 1 ? "" : "s"} painted.</span>}
        </Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          <PrimaryBtn onClick={onNext}>{availCount > 0 ? "Next: your guild →" : "Skip for now →"}</PrimaryBtn>
        </div>
      </div>
    );
  }
  if (step === "guildPrompt") {
    return (
      <div className="space-y-3">
        <Coach>
          <span className="font-bold text-white">Raids belong to guilds.</span> This is the Guild Directory, where players browse and join crews. To plan a raid you need one of your own, so let's create a quick guild. Still just a walkthrough, nothing is saved.
        </Coach>
        <div className="flex items-center justify-between gap-3">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          <div className="flex items-center gap-2">
            <button onClick={onGuildSkip} className="px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-white/50 hover:text-white hover:bg-white/5 transition-colors">Not now</button>
            <PrimaryBtn onClick={onNext}>Create a guild →</PrimaryBtn>
          </div>
        </div>
      </div>
    );
  }
  if (step === "guild") {
    return (
      <div className="space-y-3">
        <Coach>
          {guildDone ? (
            <><span className="font-bold text-white">{guildName || "Your guild"} is created, that's its card.</span> Click it to step inside, there's one thing every guild leader needs to know about.</>
          ) : (
            <><span className="font-bold text-white">Hit the highlighted</span> <span className="text-cyan-300 font-bold">+ Create Guild</span> and give it a name and tag. You'll be its owner, which unlocks raid planning.</>
          )}
        </Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          {guildDone ? <PrimaryBtn onClick={onNext}>Open my guild →</PrimaryBtn> : <PrimaryBtn onClick={onCreateGuild}>+ Create Guild</PrimaryBtn>}
        </div>
      </div>
    );
  }
  if (step === "guildInvite") {
    if (invitePhase === "code") {
      return (
        <div className="space-y-3">
          <Coach>
            <span className="font-bold text-white">This is how your guild grows.</span> Share the highlighted invite link or code in your Discord. Everyone who joins adds their own characters and raid hours, and that's the data your raids get built from. No spreadsheets, no chasing signups.
          </Coach>
          <div className="flex justify-between">
            <GhostBtn onClick={onBack}>Back</GhostBtn>
            <PrimaryBtn onClick={onInviteNext}>Got it →</PrimaryBtn>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <Coach>
          <span className="font-bold text-white">Guild sorted. Time to raid.</span> Click <span className="text-cyan-300 font-bold">Planner</span> in the top bar, that's where every raid gets scheduled.
        </Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onInviteBack}>Back</GhostBtn>
          <GhostBtn onClick={onNext}>Take me there →</GhostBtn>
        </div>
      </div>
    );
  }
  if (step === "plannerNav") {
    return (
      <div className="space-y-3">
        <Coach>
          <span className="font-bold text-white">The Raid Planner: your guild's schedule.</span> Hit the highlighted <span className="text-indigo-300 font-bold">+ Schedule Raid</span> to build one yourself. (The amber <span className="text-amber-300 font-bold">Auto Schedule</span> is ARC, it builds whole rosters from everyone's availability automatically once you're signed up.)
        </Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          <PrimaryBtn onClick={onNext}>+ Schedule Raid</PrimaryBtn>
        </div>
      </div>
    );
  }
  if (step === "raid") {
    const raidCopy = [
      <><span className="font-bold text-white">Setup: what and when.</span> Pick your instance from the template list, then just click the best <span className="text-emerald-300 font-bold">Suggested Raid Time</span> below. They're ranked by how many guildies painted those hours free, this is the availability grid paying off.</>,
      <><span className="font-bold text-white">Roster: who's coming.</span> Click or drag a spec from the highlighted pool to seat a player. Cards show every spec each player can flex to, plus their logs parse, so balancing tanks, healers and DPS takes seconds.</>,
      <><span className="font-bold text-white">Review, then go live.</span> Pick a raid leader and loot rules if you like, then hit the green <span className="text-emerald-300 font-bold">Publish</span> so your whole guild sees it. Save keeps it as a draft only officers see.</>,
    ];
    return (
      <div className="space-y-3">
        <Coach>{raidCopy[raidTab]}</Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onRaidBack}>Back</GhostBtn>
          <PrimaryBtn onClick={onRaidAdvance}>{raidTab < 2 ? "Next step →" : "Publish →"}</PrimaryBtn>
        </div>
      </div>
    );
  }
  if (step === "planner") {
    return (
      <div className="space-y-3">
        <Coach pose="confident">
          <span className="font-bold text-white">{raidName} is live. This is its command center.</span> The highlighted Roster Pool shows who can still join and exactly why others can't (locked, unconfirmed, busy). Fill seats from here, use <span className="text-cyan-300 font-bold">Check Suggestions</span> for best fits, or a <span className="text-orange-300 font-bold">PUG Invite Link</span> for outsiders. This is where you'll live on raid night.
        </Coach>
        <div className="flex justify-between">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          <PrimaryBtn onClick={onNext}>Finish up →</PrimaryBtn>
        </div>
      </div>
    );
  }
  // keep / commit: recap what they learned, then the real sign-up decision.
  return (
    <div className="space-y-3">
      <Coach pose="proud">
        <span className="font-bold text-white">That's the whole loop. You just ran a guild for two minutes:</span>
        <span className="block mt-2 space-y-1.5">
          <span className="flex items-center gap-2 text-xs text-white/70"><ChecklistIcon done /> Created {charName || "your character"}{tileSpec ? ` (${tileSpec})` : ""}</span>
          <span className="flex items-center gap-2 text-xs text-white/70"><ChecklistIcon done={availCount > 0} /> {availCount > 0 ? `Painted ${availCount} raid ${availCount === 1 ? "hour slot" : "hour slots"}` : "Raid hours (you can paint them any time from your roster)"}</span>
          {guildDone && <span className="flex items-center gap-2 text-xs text-white/70"><ChecklistIcon done /> Created {guildName || "a guild"} and found its invite code</span>}
          {raidBuilt && <span className="flex items-center gap-2 text-xs text-white/70"><ChecklistIcon done /> Scheduled, rostered and published {raidName}</span>}
        </span>
        <span className="block mt-2 text-white/60">
          Nothing is saved yet.{" "}
          {guildDone
            ? <>Sign up with Discord and choose what to take with you: character and guild together, or just the character.</>
            : <>Sign up with Discord and we'll set up your character and availability for real.</>}
        </span>
      </Coach>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onStartOver} className="px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-white/40 hover:text-red-300 hover:bg-red-500/10 transition-colors">Start over</button>
        <div className="flex items-center gap-2 flex-wrap">
          <GhostBtn onClick={onBack}>Back</GhostBtn>
          {guildDone && (
            <button onClick={() => onKeep(false)} className="px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-white/60 border border-white/15 bg-white/5 hover:bg-white/10 hover:text-white transition-colors">Just my character</button>
          )}
          <DiscordBtn onClick={() => onKeep(guildDone)}>{guildDone ? "Keep character + guild" : "Keep my character"}</DiscordBtn>
        </div>
      </div>
    </div>
  );
}

// Guild form field: a select when the schema has options, else a text input
// (mirrors GuildsPage.renderSelectOrInput). Stores the option label.
function GuildField({ label, value, onChange, options, placeholder, disabled }: {
  label: string; value: string; onChange: (v: string) => void; options: string[]; placeholder: string; disabled?: boolean;
}) {
  const isSelect = options.length > 0 || label === "Realm";
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase font-bold text-white/40 tracking-widest">{label}</label>
      {isSelect ? (
        <div className="relative">
          <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none appearance-none disabled:opacity-50 disabled:cursor-not-allowed">
            <option value="">{placeholder}</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-white/30">▼</div>
        </div>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:border-white/30 outline-none placeholder:text-white/20" placeholder={`Enter ${label}`} />
      )}
    </div>
  );
}

// Guild directory card (identical to GuildsPage.GuildCard). Read-only in the
// trial except the player's own card, which opens the guild detail stage.
function TrialGuildCard({ g, isYours, isOwner, onClick }: { g: any; isYours?: boolean; isOwner?: boolean; onClick?: () => void }) {
  const logoUrl = g.logoUrl ? (String(g.logoUrl).startsWith("http") ? g.logoUrl : `${API_URL}${g.logoUrl}`) : null;
  return (
    <div onClick={onClick} className="group cursor-pointer relative h-48 rounded-xl overflow-hidden bg-[#09090b] border border-white/10 transition-all hover:-translate-y-1 hover:shadow-2xl hover:border-white/25 flex flex-col">
      <div className="absolute inset-0 bg-[#000]">
        {logoUrl ? (
          <>
            <img src={logoUrl} alt={g.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/40 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-white/5 group-hover:bg-white/10 transition-colors">
            <span className="text-6xl font-black text-white/5 select-none">{(g.name || "?").charAt(0)}</span>
          </div>
        )}
      </div>
      <div className="absolute inset-0 p-4 flex flex-col justify-end">
        <div className="absolute top-3 left-3 flex gap-1.5">
          {isYours && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 backdrop-blur-md">Member</span>}
          {isOwner && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 backdrop-blur-md">Owner</span>}
        </div>
        <div className="space-y-0.5">
          <h3 className="text-lg font-black text-white leading-tight drop-shadow-lg truncate">{g.name}</h3>
          <div className="flex items-center gap-2 text-[11px] font-medium text-white/70">
            {g.tag && <span className="font-mono opacity-60">[{g.tag}]</span>}
            {g.region && <span>{g.region}</span>}
          </div>
        </div>
        <div className="h-px w-full bg-white/10 my-2.5" />
        <div className="flex justify-between items-end">
          <div className="space-y-0.5 text-[10px] font-medium text-white/60">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: factionColor(g.faction) }} />
              {g.faction || "Neutral"}
            </div>
            <div className="truncate max-w-[120px]">{g.serverName || "Unknown Realm"}</div>
          </div>
          <div className="text-right">
            <div className="text-base font-bold text-white leading-none">{g.memberCount || 0}</div>
            <div className="text-[8px] uppercase tracking-wider text-white/40">Characters</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Seated-slot card for the Create Raid wizard (identical to CreateRaidPage's
// RosterCard, minus the class-icon URLs which need authed game fields).
function TrialRosterCard({ m, dragActive, onRemove }: { m: any | null; dragActive?: boolean; onRemove?: () => void }) {
  const BASE_CLS = "flex-none relative h-[52px] w-full flex items-stretch gap-2 rounded-lg border px-2.5 py-2 transition-all select-none duration-100";
  if (!m) {
    return (
      <div className={`${BASE_CLS} border-dashed bg-white/[0.02] text-white/20 ${dragActive ? "border-cyan-400/50" : "border-white/10"}`}>
        <div className="w-[3px] self-stretch rounded-full flex-shrink-0 bg-white/10" />
        <span className="flex-1 text-[10px] text-white/30 uppercase tracking-widest font-medium text-center flex items-center justify-center">{dragActive ? "Drop here" : "Empty"}</span>
      </div>
    );
  }
  const roleColor = ROLE_COLORS[m.role] || "#fb923c";
  return (
    <div role="button" className={`${BASE_CLS} bg-black/60 cursor-grab hover:border-white/30 hover:bg-white/10 active:cursor-grabbing hover:shadow-lg`} style={{ borderColor: roleColor }}>
      <div className="w-[3px] self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: roleColor }} />
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-bold text-sm" style={{ color: CLASS_COLORS[m.cls] || "#FFFFFF" }}>{m.name}</span>
          {typeof m.score === "number" && (
            <span className="font-mono font-bold text-[13px] leading-none flex-shrink-0" style={{ color: getParseColor(m.score) }} title={`Warcraft Logs parse: ${m.score}`}>{m.score}</span>
          )}
          {m.you && <span className="text-[8px] font-bold uppercase tracking-wider text-cyan-300 bg-cyan-500/20 px-1 rounded shrink-0">You</span>}
          {m.pug && <span className="text-[8px] text-orange-300 bg-orange-500/20 px-1 py-0.5 rounded border border-orange-500/30 font-bold shrink-0">PUG</span>}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-white/50">
          <span className="truncate">{m.cls}</span>
          {m.player && m.player !== m.name && <><span className="opacity-30">·</span><span className="truncate">{m.player}</span></>}
        </div>
      </div>
      <div className="flex items-center flex-shrink-0">
        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ backgroundColor: `${roleColor}20`, color: roleColor }}>
          {m.spec || (m.role === "Melee DPS" ? "Melee" : m.role === "Ranged DPS" ? "Ranged" : m.role)}
        </span>
      </div>
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="flex h-5 w-5 items-center justify-center rounded text-white/30 hover:bg-white/20 hover:text-white transition-colors text-xs self-center">✕</button>
      )}
    </div>
  );
}

// Planner detail card (identical to PlannerPage's DraggableCard, minus the
// class-icon URLs which need authed game fields). `specsRow` renders the
// unassigned multi-spec icon row like the real Roster Pool cards.
function TrialDraggableCard({ m, onRemove, unavailableReason, draggable, onDragStart, onDragEnd }: {
  m: any; onRemove?: () => void; unavailableReason?: string | null; draggable?: boolean; onDragStart?: () => void; onDragEnd?: () => void;
}) {
  const color = CLASS_COLORS[m.cls] || "#fff";
  const roleColor = m.role ? (ROLE_COLORS[m.role] || "#fb923c") : null;
  const unavailable = !!unavailableReason;
  return (
    <div
      draggable={draggable && !unavailable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`
        group/card relative h-[48px] w-full flex items-center gap-3 px-3 rounded border bg-[#121214]
        ${unavailable ? "grayscale opacity-50 border-red-500/20 cursor-not-allowed" : "border-white/10 hover:border-white/20 cursor-grab active:cursor-grabbing hover:bg-white/5"}
        transition-all select-none
      `}
    >
      {unavailable && (
        <div className="absolute right-2 top-1.5 text-[8px] text-red-400 font-bold uppercase tracking-wider">{unavailableReason}</div>
      )}
      <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="text-xs font-bold truncate leading-tight" style={{ color }}>{m.name}</div>
            {m.pug && <span className="text-[9px] text-orange-300 bg-orange-500/20 px-1.5 py-0.5 rounded border border-orange-500/30 font-bold">PUG</span>}
            {m.you && <span className="text-[9px] text-cyan-300 bg-cyan-500/20 px-1.5 py-0.5 rounded border border-cyan-500/30 font-bold">YOU</span>}
            {typeof m.score === "number" && (
              <span className="font-mono font-bold text-[13px] leading-none flex-shrink-0" style={{ color: getParseColor(m.score) }} title={`Warcraft Logs parse: ${m.score}`}>{m.score}</span>
            )}
          </div>
          {m.role && roleColor ? (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ backgroundColor: `${roleColor}20`, color: roleColor }}>
              {m.spec || (m.role === "Melee DPS" ? "Melee" : m.role === "Ranged DPS" ? "Ranged" : m.role)}
            </span>
          ) : m.specsRow && m.specsRow.length > 0 ? (
            <div className="flex gap-0.5">
              {m.specsRow.slice(0, 3).map((se: any, i: number) => (
                <span key={i} className="px-1 py-0.5 rounded text-[8px] font-bold bg-white/10 text-white/60" title={`${se.spec} (${se.role})`}>
                  {getRoleIcon(se.role)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="text-[10px] text-white/40 truncate leading-tight mt-0.5 flex items-center gap-1.5">
          <span className="truncate">{m.cls}</span>
          {m.squad != null && (
            <span className="flex items-center gap-0.5 text-[9px] text-indigo-300 flex-shrink-0">
              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span className="font-bold">{m.squad}</span>
            </span>
          )}
        </div>
      </div>
      {onRemove && !unavailable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full bg-black border border-white/20 text-white/50 text-[10px] items-center justify-center hidden group-hover/card:flex hover:text-red-400"
        >✕</button>
      )}
    </div>
  );
}

// === Full-screen centered overlay (replay screens) =========================
function Overlay({ children, center }: { children: React.ReactNode; center?: boolean }) {
  return (
    <div className={`fixed inset-0 z-[200] bg-[#04060A]/95 backdrop-blur-xl flex p-6 ${center ? "items-center justify-center" : ""}`}>
      {children}
    </div>
  );
}

// === Faithful clone of RosterPage's form-group renderer ====================
interface FormCtx {
  groups: Group[];
  selectedOptions: Record<number, number | number[]>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<Record<number, number | number[]>>>;
  readyToRaid: boolean;
  setReadyToRaid: React.Dispatch<React.SetStateAction<boolean>>;
}
function FormGroups({ groups: toRender, ctx }: { groups: Group[]; ctx: FormCtx }) {
  const { groups, selectedOptions, setSelectedOptions, readyToRaid, setReadyToRaid } = ctx;
  const byKey = (k: string) => groups.find((g) => g.key === k);
  const classGroup = byKey("class");
  let selectedClassKey: string | null = null;
  if (classGroup) {
    const val = selectedOptions[classGroup.id];
    if (typeof val === "number") selectedClassKey = classGroup.options.find((o) => o.id === val)?.key || null;
  }
  const regionGroup = byKey("region");
  let selectedRegionKey: string | null = null;
  if (regionGroup) {
    const val = selectedOptions[regionGroup.id];
    if (typeof val === "number") selectedRegionKey = regionGroup.options.find((o) => o.id === val)?.key || null;
  }

  return (
    <>
      {toRender.map((group) => {
        if (!group.options.length) return null;
        const currentVal = selectedOptions[group.id];

        // Toggles (e.g. "Willing to raid lead", "Willing to handle loot"). Checked
        // first, exactly like RosterPage, so this group is never mistaken for the
        // "Ready to Raid" interest group below.
        if (group.options.every((o) => o.metadata?.ui_component === "toggle")) {
          return (
            <div key={group.id} className="space-y-2 pt-3 border-t border-white/10 mt-1">
              <label className={labelCls}>{group.label}</label>
              <div className="grid grid-cols-1 gap-2">
                {group.options.map((opt) => {
                  const isChecked = Array.isArray(currentVal) ? currentVal.includes(opt.id) : currentVal === opt.id;
                  const desc = opt.metadata?.description
                    || (opt.key === "willing_raid_lead" ? "Raid Leader" : opt.key === "willing_loot_master" ? "Master Looter" : "");
                  return (
                    <button key={opt.id} type="button" onClick={() => {
                      let next = Array.isArray(currentVal) ? [...currentVal] : currentVal ? [currentVal as number] : [];
                      if (isChecked) next = next.filter((id) => id !== opt.id); else next.push(opt.id);
                      setSelectedOptions((p) => ({ ...p, [group.id]: next }));
                    }} className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${isChecked ? "bg-cyan-500/20 border-cyan-500/50" : "bg-black/40 border-white/10 hover:border-white/20"}`}>
                      <div>
                        <span className={`text-xs font-bold ${isChecked ? "text-cyan-200" : "text-white/70"}`}>{opt.label}</span>
                        {desc && <div className="text-[10px] text-white/40">{desc}</div>}
                      </div>
                      <div className={`w-8 h-4 rounded-full relative transition-colors shrink-0 ${isChecked ? "bg-cyan-500" : "bg-white/10"}`}><div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${isChecked ? "left-[18px]" : "left-0.5"}`} /></div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }

        if (group.key === "role") return null;

        if (group.multiSelect) {
          if (group.key === "raid_interest" || group.key === "raid_preferences") {
            return (
              <div key={group.id} className="space-y-3 pt-3 border-t border-white/10 mt-1">
                <label className="flex items-center gap-3 cursor-pointer" onClick={(e) => {
                  e.preventDefault();
                  if (readyToRaid) { setReadyToRaid(false); setSelectedOptions((p) => { const n = { ...p }; delete n[group.id]; return n; }); }
                  else setReadyToRaid(true);
                }}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${readyToRaid ? "bg-emerald-500 border-emerald-400" : "border-white/30"}`}>
                    {readyToRaid && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <div><span className="text-xs font-bold text-white/80">Ready to Raid</span><p className="text-[10px] text-white/30">Pick which raids you want.</p></div>
                </label>
                {readyToRaid && (
                  <div className="flex flex-wrap gap-2 pl-8">
                    {group.options.map((opt) => {
                      const isSel = Array.isArray(currentVal) ? currentVal.includes(opt.id) : currentVal === opt.id;
                      return (
                        <button key={opt.id} type="button" onClick={() => {
                          let next = Array.isArray(currentVal) ? [...currentVal] : currentVal ? [currentVal as number] : [];
                          if (isSel) next = next.filter((id) => id !== opt.id); else next.push(opt.id);
                          setSelectedOptions((p) => ({ ...p, [group.id]: next }));
                        }} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${isSel ? "bg-emerald-500/20 border-emerald-500 text-emerald-200" : "bg-black/40 border-white/10 text-white/40 hover:text-white/60"}`}>{opt.label}</button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const visible = group.options.filter((opt) => {
            if (group.key === "spec") {
              const allowed = opt.metadata?.allowed_class_keys;
              if (!selectedClassKey) return false;
              return allowed && allowed.length > 0 ? allowed.includes(selectedClassKey) : false;
            }
            return true;
          });
          if (!visible.length) return null;
          return (
            <div key={group.id} className="space-y-2">
              <label className={labelCls}>{group.label}</label>
              <div className="flex flex-wrap gap-2">
                {visible.map((opt) => {
                  const isSel = Array.isArray(currentVal) ? currentVal.includes(opt.id) : currentVal === opt.id;
                  const color = opt.metadata?.role_color || "#22d3ee";
                  return (
                    <button key={opt.id} type="button" onClick={() => {
                      let next = Array.isArray(currentVal) ? [...currentVal] : currentVal ? [currentVal as number] : [];
                      if (isSel) next = next.filter((id) => id !== opt.id); else next.push(opt.id);
                      setSelectedOptions((p) => ({ ...p, [group.id]: next }));
                    }} style={isSel ? { borderColor: color, backgroundColor: `${color}33`, color: "#fff" } : {}} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${isSel ? "" : "bg-black/40 border-white/10 text-white/60 hover:text-white"}`}>{opt.label}</button>
                  );
                })}
              </div>
            </div>
          );
        }

        let visible = group.options;
        if (group.key === "realm") {
          if (!selectedRegionKey) visible = [];
          else visible = group.options.filter((opt) => { const a = opt.metadata?.allowed_region_keys; return !a || a.includes(selectedRegionKey); });
        }
        return (
          <div key={group.id} className="space-y-1.5">
            <label className={labelCls}>{group.label}</label>
            <div className="relative">
              <select value={typeof currentVal === "number" ? currentVal : ""} onChange={(e) => {
                const val = Number(e.target.value);
                if (group.key === "class") { const specG = byKey("spec"); if (specG) setSelectedOptions((p) => { const n = { ...p }; delete n[specG.id]; return n; }); }
                if (group.key === "region") { const realmG = byKey("realm"); if (realmG) setSelectedOptions((p) => { const n = { ...p }; delete n[realmG.id]; return n; }); }
                setSelectedOptions((p) => ({ ...p, [group.id]: val }));
              }} className={selectCls}>
                <option value="">Select...</option>
                {visible.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-white/30">▼</div>
            </div>
          </div>
        );
      })}
    </>
  );
}

// === presentational ========================================================
function StepIcon({ status }: { status: ReplayStep["status"] }) {
  if (status === "ok") return <svg className="w-5 h-5 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>;
  if (status === "failed") return <svg className="w-5 h-5 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>;
  if (status === "running") return <span className="w-5 h-5 shrink-0 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />;
  return <span className="w-5 h-5 shrink-0 rounded-full border-2 border-white/20" />;
}
